import {
  Autocomplete,
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  DataTable,
  Divider,
  InlineGrid,
  InlineStack,
  Layout,
  Modal,
  Page,
  Select,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  type CreateStockyNoteRevisionInput,
  type CreateStockyProductMappingResponse,
  type StockyDataChildExportResource,
  type StockyDataCatalogResource,
  type StockyDataChildren,
  type StockyDataFieldDisplay,
  type StockyDataGenericSource,
  type StockyDataNoteSlot,
  type StockyDataPlanMask,
  type StockyDataRecord,
  type StockyDataRecordId,
  type StockyDataRecordList,
  type StockyDataResource,
  type StockyDataSourceDownload,
  type StockyHistorySourceRow,
  type StockyCurrentVariant,
  type StockyMismatchDraftProduct,
  type StockyProductMappingRevision,
  type StockyProductMismatch,
  type StockyNoteKind,
  type StockyNoteRevision,
  type StockyPurchaseOrderMoneyAggregate,
  type StockyPurchaseOrderStatusFilter,
  createStockyProductMappingRevision,
  createOrReconcileStockyMismatchDraftProduct,
  completeLegacyStockyMismatchDraftCatalogMapping,
  createStockyNoteRevision,
  downloadStockyDataChildrenExport,
  downloadStockyDataExport,
  encodeStockyDataRecordId,
  getStockyDataCatalog,
  getStockyDataRecord,
  getStockyPurchaseOrderDashboardSummary,
  isStockyDataResource,
  isStockyDataRecordId,
  listStockyDataChildren,
  listStockyDataRecords,
  listStockyProductMappingRevisions,
  listStockyProductMismatches,
  listStockyNoteRevisions,
  parseStockyDataRecordId,
  searchStockyCurrentVariants,
  syncStockyMismatchDraftProductToCatalog,
} from "../api/stocky_data";
import {
  ApiError,
  assertCurrentShopGeneration,
  ShopGenerationChangedError,
} from "../api/client";
import { editShopifyProductWithAdminIntent } from "../api/shopify_intents";
import {
  ensureShopifyOptionalScope,
  ShopifyOptionalScopeError,
} from "../api/shopify_scopes";
import {
  canonicalSavedDraftSourceId,
  listSavedPurchaseOrderDrafts,
  type SavedDraftSourceId,
} from "../api/saved_purchase_order_drafts";
import { canonicalStockyPublicBigIntId } from "../api/stocky_public_ids";
import {
  type DownloadedFile,
  downloadStockySourceArchive,
} from "../api/stocky_portability";
import {
  type VerifiedShopGeneration,
  stockyEntitlementGeneration,
  useVerifiedEntitlementGeneration,
  useVerifiedShopGeneration,
} from "../ShopGenerationBoundary";
import { getMe } from "../api/me";
import { currentLocale, t } from "../lib/i18n";
import { formatDateTime } from "../utils/date";
import { displayVariantTitle } from "../utils/productTitles";
import { money } from "../utils/money";
import {
  clearPersistentIdempotencyAttempt,
  persistentIdempotencyAttempt,
  type PersistentIdempotencyAttempt,
} from "../utils/idempotency";

interface ResourceUi {
  label: string;
  description: string;
}

const RESOURCE_UI: Record<StockyDataResource, ResourceUi> = {
  "tax-types": {
    label: "Stockyの仕入税区分",
    description: "保存した仕入用の税区分です。",
  },
  "shop-tax-rates": {
    label: "現在の仕入税率",
    description: "取り込み後の編集を含む、UreyukiBoxで現在使う仕入税率です。",
  },
  suppliers: {
    label: "仕入先",
    description: "保存した仕入先と連絡先です。",
  },
  "purchase-orders": {
    label: "発注書",
    description: "保存した発注書と明細です。",
  },
  "goods-receipts": {
    label: "入荷履歴",
    description: "発注書ごとに記録した入荷日時・拠点・数量・メモです。",
  },
  "stock-adjustments": {
    label: "在庫調整",
    description: "保存した一般の在庫調整です。棚卸し履歴とは別です。",
  },
  stocktakes: {
    label: "棚卸し履歴",
    description: "保存した棚卸しと、その商品明細です。",
  },
  "stocktake-items": {
    label: "棚卸し明細",
    description: "棚卸しごとの商品と源泉数量です。",
  },
  "reorder-policies": {
    label: "発注点・リードタイム・MOQ",
    description:
      "UreyukiBoxで現在使っている発注点、リードタイム、最低発注数量です（取込後の変更を含みます）。",
  },
  "variant-suppliers": {
    label: "商品と仕入先の紐付け",
    description:
      "UreyukiBoxで現在使っている商品と仕入先、仕入条件の紐付けです（取込後の変更を含みます）。",
  },
  "source-metadata": {
    label: "保存済み原本",
    description: "保存したCSV・貼り付け原文と解析状況です。",
  },
  "product-mismatches": {
    label: "商品不整合",
    description: "保存データと現在のShopify商品が一致しない項目を確認します。",
  },
};

const DATA_SECTIONS: Array<{
  key: string;
  title: string;
  description: string;
  resources: StockyDataResource[];
}> = [
  {
    key: "purchase-orders",
    title: "発注書",
    description: "発注書、明細、入荷履歴、支払予定",
    resources: ["purchase-orders", "goods-receipts"],
  },
  {
    key: "suppliers",
    title: "仕入先",
    description: "仕入先と連絡先",
    resources: ["suppliers"],
  },
  {
    key: "stocktakes",
    title: "棚卸し",
    description: "棚卸し履歴と商品明細",
    resources: ["stocktakes"],
  },
  {
    key: "stock-adjustments",
    title: "在庫調整",
    description: "一般の在庫調整と明細",
    resources: ["stock-adjustments"],
  },
  {
    key: "replenishment-settings",
    title: "発注設定",
    description: "発注点・リードタイム・MOQ・仕入税区分・商品と仕入先",
    resources: [
      "reorder-policies",
      "tax-types",
      "shop-tax-rates",
      "variant-suppliers",
    ],
  },
  {
    key: "product-mismatches",
    title: "商品不整合",
    description: "保存データと現在のShopify商品の対応を確認",
    resources: ["product-mismatches"],
  },
  {
    key: "saved-sources",
    title: "保存レポート・原本",
    description: "保存したレポート、CSV、貼り付け原文の内容",
    resources: ["source-metadata"],
  },
];

const FIELD_LABELS: Record<string, string> = {
  workflow_status: "状態",
  status: "状態",
  resource: "項目",
  item_count: "件数",
  name: "名前",
  company_name: "会社名",
  contact_name: "担当者",
  contact_email: "メールアドレス",
  phone: "電話番号",
  number: "発注書番号",
  purchase_order_number: "発注書番号",
  supplier_name: "仕入先",
  invoice_number: "請求書番号",
  ordered_at: "発注日",
  expected_on: "入荷予定日",
  received_at: "入荷日時",
  total_quantity: "入荷数量",
  quantity: "入荷数量",
  ordered_qty: "発注数量",
  paid: "支払",
  archived: "アーカイブ",
  currency: "通貨",
  shipping: "送料",
  shipping_location: "配送先",
  supplier_order_number: "仕入先注文番号",
  paid_on: "支払日",
  adjustments: "調整額",
  tax: "税額",
  subtotal: "小計",
  received_subtotal: "入荷分小計",
  total: "合計",
  received: "入荷状況",
  first_received: "初回入荷日",
  last_received: "最終入荷日",
  location: "拠点",
  notes: "メモ",
  employee: "担当者",
  reason: "理由",
  adjusted_at: "調整日時",
  location_name: "拠点",
  note: "メモ",
  started_at: "開始日時",
  applied_at: "適用日時",
  sku: "SKU",
  title: "商品",
  variant_title: "バリエーション",
  variant_record_title: "保存時の商品バリエーション名",
  expected_qty: "システム在庫",
  counted_qty: "実数",
  source_current_stock: "Stocky Current stock",
  source_adjustment_qty: "Stocky Adjustment",
  reorder_point: "発注点",
  lead_time_days: "リードタイム",
  min_order_qty: "最低発注数量",
  supplier: "仕入先",
  filename: "ファイル名",
  category: "カテゴリ",
  row_count: "行数",
  created_at: "保存日時",
  updated_at: "更新日時",
  report_category: "レポートカテゴリ",
  report_name: "レポート名",
  report_type: "レポート種別",
  source_kind: "原本種別",
  capture_method: "取得方法",
  current_product_resolution: "現在の商品としての確認",
  current_variant_id: "現在の商品ID（UreyukiBox）",
  current_shopify_product_id: "現在のShopify商品ID",
  current_shopify_variant_id: "現在のShopifyバリアントID",
  current_shopify_inventory_item_id: "現在のShopify在庫アイテムID",
  current_sku: "現在のSKU",
  current_display_title: "現在の商品名",
  current_product_title: "現在の商品名",
  current_variant_title: "現在のバリエーション名",
};

const PRODUCT_IDENTITY_OVERLAY_FIELD_KEYS = new Set([
  "current_product_resolution",
  "current_variant_id",
  "current_shopify_product_id",
  "current_shopify_variant_id",
  "current_shopify_inventory_item_id",
  "current_sku",
  "current_display_title",
  "current_product_title",
  "current_variant_title",
]);
const MERCHANT_HIDDEN_SOURCE_FIELD_KEYS = new Set([
  "source_import_id",
  "evidence_fidelity",
  "coverage_status",
  "normalization_level",
  "source_archive_id",
  "source_locator",
  "source_note_revision_id",
  "actor_shopify_user_id",
  "body_sha256",
  "supersedes_id",
  "request_sha256",
  "provenance",
  "source_mapping",
  "source_input_revision",
  "archive_by_source_import_id",
  "api_migration_evidence",
  "stocky_po_id",
  "stocky_adjustment_id",
  "supplier_external_id",
  "stocky_created_at",
  "stocky_updated_at",
  "migrated_at",
  "source_kind",
  "capture_method",
]);

const NOTE_KIND_LABELS: Record<StockyNoteKind, string> = {
  supplier_private_note: "仕入先メモ",
  supplier_po_note_template: "仕入先の発注書用メモ",
  purchase_order_note: "発注書メモ",
  internal_note: "社内メモ",
};

function resourceLabel(resource: StockyDataResource): string {
  return t(RESOURCE_UI[resource].label);
}

function fieldLabel(key: string): string {
  return t(FIELD_LABELS[key] ?? key);
}

function noteKindLabel(kind: StockyNoteKind): string {
  return t(NOTE_KIND_LABELS[kind]);
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.detail;
  return t("データを取得できませんでした。時間をおいて再試行してください。");
}

function isStaleStockyDataCursor(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 409 &&
    error.code === "stocky_data_cursor_stale"
  );
}

function isStockyNoteConflict(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

function retryStockyDataPage(failureCount: number, error: unknown): boolean {
  return !isStaleStockyDataCursor(error) && failureCount < 3;
}

/** Keep object URLs alive long enough for the browser download handoff. */
function useDownloadHandoff(): (download: DownloadedFile) => void {
  const pendingRef = useRef(new Map<string, number>());
  const activeRef = useRef(false);
  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
      for (const [url, timer] of pendingRef.current) {
        window.clearTimeout(timer);
        URL.revokeObjectURL(url);
      }
      pendingRef.current.clear();
    };
  }, []);
  return useCallback((download: DownloadedFile) => {
    if (!activeRef.current) return;
    const url = URL.createObjectURL(download.blob);
    try {
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = download.filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      const timer = window.setTimeout(() => {
        pendingRef.current.delete(url);
        URL.revokeObjectURL(url);
      }, 60_000);
      pendingRef.current.set(url, timer);
    } catch (error) {
      URL.revokeObjectURL(url);
      throw error;
    }
  }, []);
}

function shopSearch(search: string, extras?: Record<string, string>): string {
  const current = new URLSearchParams(search);
  const next = new URLSearchParams();
  for (const key of ["shop", "host", "locale", "embedded", "session"]) {
    const value = current.get(key);
    if (value !== null) next.set(key, value);
  }
  for (const [key, value] of Object.entries(extras ?? {})) next.set(key, value);
  const encoded = next.toString();
  return encoded ? `?${encoded}` : "";
}

function currentProductResolutionLabel(value: string): string {
  if (value === "current") return t("現在のShopify商品");
  if (value === "auto_matched") return t("安全な一致候補あり");
  if (value === "mapped") return t("確認した現在の商品");
  if (value === "not_current") return t("現在の商品には該当しない");
  if (value === "target_stale") return t("以前に確認した商品を再確認してください");
  if (value === "unresolved") return t("未解決");
  return t("現在の商品としての確認状態を確認してください");
}

function isProductIdentityOverlayField(field: StockyDataFieldDisplay): boolean {
  return PRODUCT_IDENTITY_OVERLAY_FIELD_KEYS.has(field.key);
}

function isUnmaskedField(
  record: StockyDataRecord,
  field: StockyDataFieldDisplay,
): boolean {
  return (
    field.state !== "masked" &&
    field.masking.tier === "none" &&
    !record.masked_fields.includes(field.key)
  );
}

function currentProductFieldText(
  record: StockyDataRecord,
  key:
    "current_display_title" | "current_product_title" | "current_variant_title",
): string | null {
  const field = record.fields.find((candidate) => candidate.key === key);
  return field && isUnmaskedField(record, field)
    ? field.display_text.trim() || null
    : null;
}

function currentProductDisplayTitle(record: StockyDataRecord): string | null {
  const canonicalTitle = currentProductFieldText(
    record,
    "current_display_title",
  );
  if (canonicalTitle && canonicalTitle !== "Default Title") {
    return canonicalTitle;
  }
  const productTitle = currentProductFieldText(record, "current_product_title");
  const rawVariantTitle = currentProductFieldText(
    record,
    "current_variant_title",
  );
  const variantTitle =
    rawVariantTitle === "Default Title" ? null : rawVariantTitle;
  return displayVariantTitle(productTitle, variantTitle);
}

function isRedundantVariantTitleField(
  record: StockyDataRecord,
  field: StockyDataFieldDisplay,
): boolean {
  const value = field.display_text.trim();
  if (
    (field.key === "variant_title" || field.key === "current_variant_title") &&
    value === "Default Title"
  ) {
    return true;
  }
  if (field.key !== "variant_title") return false;
  return (
    value !== "" &&
    (value === currentProductFieldText(record, "current_product_title") ||
      value === currentProductDisplayTitle(record))
  );
}

function isMerchantVisibleField(
  record: StockyDataRecord,
  field: StockyDataFieldDisplay,
): boolean {
  return (
    isUnmaskedField(record, field) &&
    !isRedundantVariantTitleField(record, field) &&
    !MERCHANT_HIDDEN_SOURCE_FIELD_KEYS.has(field.key)
  );
}

function isMerchantTableField(
  record: StockyDataRecord,
  field: StockyDataFieldDisplay,
): boolean {
  return (
    isMerchantVisibleField(record, field) &&
    !isProductIdentityOverlayField(field)
  );
}

function merchantPrimaryText(record: StockyDataRecord): string {
  return record.primary_text.trim() === "Default Title"
    ? (currentProductDisplayTitle(record) ?? "—")
    : record.primary_text;
}

function searchTextMatches(value: string, query: string): boolean {
  const needle = query.trim().toLocaleLowerCase();
  return needle !== "" && value.toLocaleLowerCase().includes(needle);
}

function fieldDisplayText(field: StockyDataFieldDisplay): string {
  return field.key === "current_product_resolution"
    ? currentProductResolutionLabel(field.display_text)
    : field.display_text;
}

function FieldValue({ field }: { field: StockyDataFieldDisplay }) {
  return (
    <BlockStack gap="050">
      <InlineStack gap="150" blockAlign="center" wrap={false}>
        <Text as="span">{fieldDisplayText(field)}</Text>
        {field.masking.tier === "plan" && (
          <Badge tone="attention">{t("プランにより非表示")}</Badge>
        )}
      </InlineStack>
      {field.masking.reason && (
        <Text as="span" variant="bodySm" tone="subdued">
          {field.masking.reason}
        </Text>
      )}
    </BlockStack>
  );
}

function CurrentProductOverlay({ record }: { record: StockyDataRecord }) {
  const fields = record.fields.filter(
    (field) =>
      isProductIdentityOverlayField(field) &&
      isMerchantVisibleField(record, field),
  );
  if (fields.length === 0) return null;
  const resolution = fields.find(
    (field) => field.key === "current_product_resolution",
  );
  const identityFields = fields.filter(
    (field) =>
      field.key !== "current_product_resolution" &&
      field.key !== "current_display_title" &&
      field.key !== "current_product_title" &&
      field.key !== "current_variant_title",
  );
  const title = currentProductDisplayTitle(record);
  return (
    <Box padding="200" background="bg-surface-secondary" borderRadius="200">
      <BlockStack gap="100">
        <Text as="span" variant="bodySm" fontWeight="semibold">
          {t("現在の商品としての確認")}:{" "}
          {resolution ? fieldDisplayText(resolution) : "—"}
        </Text>
        {identityFields.map((field) => (
          <Text key={field.key} as="span" variant="bodySm">
            {fieldLabel(field.key)}: {fieldDisplayText(field)}
          </Text>
        ))}
        {title && (
          <Text as="span" variant="bodySm">
            {fieldLabel("current_display_title")}: {title}
          </Text>
        )}
      </BlockStack>
    </Box>
  );
}

function QueryError({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <Banner
      tone="critical"
      title={t("データを取得できませんでした")}
      action={{ content: t("再試行"), onAction: retry }}
    >
      <p>{errorMessage(error)}</p>
    </Banner>
  );
}

function ResourceNavigation({
  selected,
  catalog,
  navigateTo,
}: {
  selected: StockyDataResource | null;
  catalog: StockyDataCatalogResource[];
  navigateTo: (resource: StockyDataResource) => void;
}) {
  const byKey = new Map(catalog.map((resource) => [resource.key, resource]));
  return (
    <BlockStack gap="400">
      {DATA_SECTIONS.map((group) => (
        <BlockStack key={group.key} gap="200">
          <BlockStack gap="050">
            <Text as="h2" variant="headingSm">
              {t(group.title)}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {t(group.description)}
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            {group.resources.map((resource) => {
              const info = byKey.get(resource);
              return (
                <Button
                  key={resource}
                  variant={selected === resource ? "primary" : "plain"}
                  textAlign="left"
                  onClick={() => navigateTo(resource)}
                >
                  {t("{label}（{count}件）", {
                    label: resourceLabel(resource),
                    count: info?.count ?? 0,
                  })}
                </Button>
              );
            })}
          </BlockStack>
        </BlockStack>
      ))}
    </BlockStack>
  );
}

function RecordsTable({
  records,
  onOpen,
  query = "",
}: {
  records: StockyDataRecord[];
  onOpen?: (id: StockyDataRecordId) => void;
  query?: string;
}) {
  const fieldKeys = useMemo(() => {
    const keys: string[] = [];
    const matchingKeys: string[] = [];
    for (const record of records) {
      for (const field of record.fields) {
        if (!isMerchantTableField(record, field)) continue;
        if (!keys.includes(field.key)) keys.push(field.key);
        if (
          searchTextMatches(field.display_text, query) &&
          !matchingKeys.includes(field.key)
        ) {
          matchingKeys.push(field.key);
        }
      }
    }
    return [
      ...matchingKeys,
      ...keys.filter((key) => !matchingKeys.includes(key)),
    ].slice(0, 4);
  }, [query, records]);

  if (records.length === 0) {
    return (
      <Text as="p" tone="subdued">
        {t("保存済みのデータはありません。")}
      </Text>
    );
  }
  return (
    <DataTable
      columnContentTypes={[
        "text",
        ...fieldKeys.map(() => "text" as const),
        ...(onOpen ? (["text"] as const) : []),
      ]}
      headings={[
        t("名前・識別子"),
        ...fieldKeys.map(fieldLabel),
        ...(onOpen ? [t("詳細")] : []),
      ]}
      rows={records.map((record) => {
        const fields = new Map(
          record.fields.map((field) => [field.key, field]),
        );
        return [
          <BlockStack key={record.id} gap="200">
            <Text as="span">{merchantPrimaryText(record)}</Text>
            <CurrentProductOverlay record={record} />
          </BlockStack>,
          ...fieldKeys.map((key) => {
            const field = fields.get(key);
            return field && isMerchantTableField(record, field) ? (
              <FieldValue field={field} />
            ) : (
              "—"
            );
          }),
          ...(onOpen
            ? [
                <Button
                  key={record.id}
                  variant="plain"
                  onClick={() => onOpen(record.id)}
                >
                  {t("詳細を見る")}
                </Button>,
              ]
            : []),
        ];
      })}
    />
  );
}

const PURCHASE_ORDER_STATUS_OPTIONS: Array<{
  value: StockyPurchaseOrderStatusFilter;
  label: string;
}> = [
  { value: "all", label: "すべて" },
  { value: "draft", label: "下書き" },
  { value: "ordered", label: "発注済み" },
  { value: "archived", label: "アーカイブ" },
  { value: "awaiting_payment", label: "支払待ち" },
  { value: "overdue", label: "支払期限超過" },
];

function purchaseOrderStatusLabel(value: string): string {
  if (value === "placed") return t("発注済み");
  if (value === "partially_received") return t("一部入荷");
  if (value === "received") return t("入荷完了");
  if (value === "cancelled") {
    // 「キャンセル」は操作ボタンでは Cancel、状態では Cancelled になる。
    return currentLocale() === "ja" ? "キャンセル" : t("取り消し");
  }
  return t(
    PURCHASE_ORDER_STATUS_OPTIONS.find((option) => option.value === value)
      ?.label ?? "状態不明",
  );
}

function compactField(record: StockyDataRecord, key: string): string {
  const field = record.fields.find((candidate) => candidate.key === key);
  return field?.state === "present" &&
    field.display_text &&
    isMerchantTableField(record, field)
    ? field.display_text
    : "—";
}

const PURCHASE_ORDER_TABLE_FIELD_KEYS = new Set([
  "number",
  "supplier_name",
  "workflow_status",
  "invoice_number",
  "ordered_at",
  "expected_on",
  "payment_due_on",
]);

function purchaseOrderSearchMatch(
  record: StockyDataRecord,
  query: string,
): StockyDataFieldDisplay | null {
  if (query.trim() === "") return null;
  return (
    record.fields.find(
      (field) =>
        isMerchantTableField(record, field) &&
        !PURCHASE_ORDER_TABLE_FIELD_KEYS.has(field.key) &&
        searchTextMatches(field.display_text, query),
    ) ?? null
  );
}

function PurchaseOrderRecordsTable({
  records,
  onOpen,
  query,
}: {
  records: StockyDataRecord[];
  onOpen: (id: StockyDataRecordId) => void;
  query: string;
}) {
  if (records.length === 0) {
    return (
      <Text as="p" tone="subdued">
        {t("この状態の発注書はありません。")}
      </Text>
    );
  }
  return (
    <DataTable
      columnContentTypes={[
        "text",
        "text",
        "text",
        "text",
        "text",
        "text",
        "text",
        "text",
      ]}
      headings={[
        t("発注書番号"),
        t("仕入先"),
        t("状態"),
        t("請求書番号"),
        t("発注日"),
        t("入荷予定日"),
        t("支払期日"),
        t("詳細"),
      ]}
      rows={records.map((record) => {
        const workflowStatus = compactField(record, "workflow_status");
        const number =
          compactField(record, "number") === "—"
            ? merchantPrimaryText(record)
            : compactField(record, "number");
        const searchMatch = purchaseOrderSearchMatch(record, query);
        return [
          searchMatch ? (
            <BlockStack key={`number-${record.id}`} gap="050">
              <Text as="span">{number}</Text>
              <Text as="span" variant="bodySm" tone="subdued">
                {fieldLabel(searchMatch.key)}: {fieldDisplayText(searchMatch)}
              </Text>
            </BlockStack>
          ) : (
            number
          ),
          compactField(record, "supplier_name"),
          <Badge
            key={`status-${record.id}`}
            tone={workflowStatus === "archived" ? undefined : "info"}
          >
            {purchaseOrderStatusLabel(workflowStatus)}
          </Badge>,
          compactField(record, "invoice_number"),
          compactField(record, "ordered_at"),
          compactField(record, "expected_on"),
          compactField(record, "payment_due_on"),
          <Button
            key={record.id}
            variant="plain"
            onClick={() => onOpen(record.id)}
          >
            {t("詳細を見る")}
          </Button>,
        ];
      })}
    />
  );
}

function AggregateAmounts({
  aggregate,
}: {
  aggregate: StockyPurchaseOrderMoneyAggregate;
}) {
  return (
    <BlockStack gap="050">
      {aggregate.amounts.length === 0 ? (
        <Text as="p" variant="headingLg">
          {aggregate.count === 0 ? "0" : t("金額不明")}
        </Text>
      ) : (
        aggregate.amounts.map(({ currency, amount }) => (
          <Text as="p" variant="headingLg" key={currency ?? "unknown-currency"}>
            {currency
              ? money(amount, currency)
              : t("通貨不明: {amount}", { amount })}
          </Text>
        ))
      )}
      <Text as="p" variant="bodySm" tone="subdued">
        {t("{count}件", { count: aggregate.count })}
        {aggregate.unknown_amount_count > 0
          ? t("（うち金額不明 {count}件）", {
              count: aggregate.unknown_amount_count,
            })
          : ""}
      </Text>
    </BlockStack>
  );
}

function PurchaseOrderDashboard({
  shop,
  onSelectStatus,
}: {
  shop: VerifiedShopGeneration;
  onSelectStatus: (status: StockyPurchaseOrderStatusFilter) => void;
}) {
  const summaryQuery = useQuery({
    queryKey: ["stocky-data", shop.shopGeneration, "purchase-order-summary", 8],
    queryFn: () => getStockyPurchaseOrderDashboardSummary(shop.shopDomain, 8),
  });
  const summary = summaryQuery.data;
  const maxBucketCount = Math.max(
    1,
    ...(summary?.upcoming.buckets.map((bucket) => bucket.count) ?? []),
  );

  if (summaryQuery.isError) {
    return (
      <QueryError
        error={summaryQuery.error}
        retry={() => void summaryQuery.refetch()}
      />
    );
  }
  if (summaryQuery.isLoading || !summary) {
    return <SkeletonBodyText lines={6} />;
  }

  const cards: Array<{
    status: "draft" | "awaiting_payment" | "overdue";
    title: string;
  }> = [
    { status: "draft", title: "下書き" },
    { status: "awaiting_payment", title: "支払待ち" },
    { status: "overdue", title: "支払期限超過" },
  ];

  return (
    <BlockStack gap="400">
      <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
        {cards.map((card) => (
          <Card key={card.status}>
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                {t(card.title)}
              </Text>
              <AggregateAmounts aggregate={summary.statuses[card.status]} />
              <Button
                variant="plain"
                onClick={() => onSelectStatus(card.status)}
              >
                {t("一覧を見る")}
              </Button>
            </BlockStack>
          </Card>
        ))}
      </InlineGrid>

      <Card>
        <BlockStack gap="300">
          <BlockStack gap="050">
            <Text as="h3" variant="headingMd">
              {t("今後の支払予定")}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {t(
                "Stockyに保存された支払期日を今後{weeks}週間、週ごとに表示します。",
                {
                  weeks: summary.horizon_weeks,
                },
              )}
            </Text>
          </BlockStack>
          {summary.upcoming.buckets.length === 0 ? (
            <Box
              minHeight="140px"
              padding="400"
              background="bg-surface-secondary"
              borderRadius="300"
            >
              <Text as="p" alignment="center" tone="subdued">
                {t("期日が分かる今後の支払予定はありません。")}
              </Text>
            </Box>
          ) : (
            <div
              data-testid="stocky-upcoming-payments-chart"
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${summary.upcoming.buckets.length}, minmax(92px, 1fr))`,
                gap: "12px",
                alignItems: "end",
                overflowX: "auto",
                minHeight: "190px",
                paddingTop: "12px",
              }}
            >
              {summary.upcoming.buckets.map((bucket) => (
                <BlockStack
                  key={bucket.week_start}
                  gap="100"
                  inlineAlign="center"
                >
                  <AggregateAmounts aggregate={bucket} />
                  <div
                    aria-hidden="true"
                    style={{
                      width: "48px",
                      height: `${Math.max(12, (bucket.count / maxBucketCount) * 84)}px`,
                      borderRadius: "8px 8px 2px 2px",
                      background: "var(--p-color-bg-fill-brand, #008060)",
                    }}
                  />
                  <Text as="p" variant="bodySm" tone="subdued">
                    {bucket.week_start}
                  </Text>
                </BlockStack>
              ))}
            </div>
          )}
          {summary.upcoming.missing_due_date.count > 0 && (
            <Banner tone="info" title={t("支払期日がない発注書があります")}>
              <p>
                {t(
                  "{count}件は金額を保存していますが、期日を推測せずグラフから外しています。",
                  {
                    count: summary.upcoming.missing_due_date.count,
                  },
                )}
              </p>
            </Banner>
          )}
        </BlockStack>
      </Card>
    </BlockStack>
  );
}

const LOCKED_PREVIEW_FALLBACK_FIELDS: Partial<
  Record<StockyDataResource, string[]>
> = {
  "reorder-policies": [
    "sku",
    "variant_title",
    "reorder_point",
    "lead_time_days",
  ],
};

function LockedRowSilhouette({ row }: { row: number }) {
  const widths = [68, 92, 76, 58, 84];
  return (
    <span
      role="img"
      aria-label={t("プランにより非表示")}
      style={{
        display: "inline-block",
        width: `${widths[row % widths.length]}px`,
        maxWidth: "100%",
        height: "10px",
        borderRadius: "999px",
        background: "var(--p-color-bg-fill-tertiary, #dfe3e8)",
      }}
    />
  );
}

function LockedPreviewTable({
  resource,
  mask,
}: {
  resource: StockyDataResource;
  mask: StockyDataPlanMask;
}) {
  if (mask.count === 0) {
    return (
      <Text as="p" tone="subdued">
        {t("保存済みのデータはありません。")}
      </Text>
    );
  }

  const fieldKeys = (
    mask.preview_fields ??
    LOCKED_PREVIEW_FALLBACK_FIELDS[resource] ??
    []
  ).slice(0, 4);
  const columns = `minmax(160px, 1.4fr) repeat(${fieldKeys.length}, minmax(120px, 1fr))`;
  const cellStyle = {
    minWidth: 0,
    padding: "12px 14px",
    borderBottom: "1px solid var(--p-color-border-secondary, #dfe3e8)",
  } as const;

  return (
    <BlockStack gap="200">
      <div
        style={{
          position: "relative",
          overflowX: "auto",
          border: "1px solid var(--p-color-border-secondary, #dfe3e8)",
          borderRadius: "10px",
          background: "var(--p-color-bg-surface, #fff)",
        }}
      >
        <div style={{ minWidth: `${160 + fieldKeys.length * 120}px` }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: columns,
              fontWeight: 600,
              background: "var(--p-color-bg-surface-secondary, #f7f7f7)",
            }}
          >
            <div style={cellStyle}>{t("名前・識別子")}</div>
            {fieldKeys.map((key) => (
              <div key={key} style={cellStyle}>
                {fieldLabel(key)}
              </div>
            ))}
          </div>
          <div
            style={{ display: "grid", gridTemplateColumns: columns }}
            data-testid="stocky-locked-synthetic-row"
          >
            <div style={cellStyle}>
              <LockedRowSilhouette row={0} />
            </div>
            {fieldKeys.map((key, index) => (
              <div key={key} style={cellStyle}>
                <LockedRowSilhouette row={index + 1} />
              </div>
            ))}
          </div>
          {mask.count > 1 && (
            <div
              style={{ display: "grid", gridTemplateColumns: columns }}
              data-testid="stocky-locked-silhouette-row"
            >
              <div style={cellStyle}>
                <LockedRowSilhouette row={2} />
              </div>
              {fieldKeys.map((key, index) => (
                <div key={key} style={cellStyle}>
                  <LockedRowSilhouette row={index + 3} />
                </div>
              ))}
            </div>
          )}
        </div>
        <div
          aria-hidden="true"
          data-testid="stocky-locked-gradient"
          style={{
            position: "absolute",
            inset: "42px 0 0",
            pointerEvents: "none",
            background:
              "linear-gradient(to bottom, color-mix(in srgb, var(--p-color-bg-surface, #fff) 14%, transparent) 0%, color-mix(in srgb, var(--p-color-bg-surface, #fff) 28%, transparent) 36%, color-mix(in srgb, var(--p-color-bg-surface, #fff) 76%, transparent) 72%, var(--p-color-bg-surface, #fff) 100%)",
          }}
        />
      </div>
      <Text as="p" variant="bodySm" tone="subdued">
        {t(
          "固定プレースホルダを表示しています。保存済み {count} 件の実際の値はブラウザへ送信していません。",
          { count: mask.count },
        )}
      </Text>
    </BlockStack>
  );
}

function catalogPlanMask(
  catalogResource: StockyDataCatalogResource | undefined,
): StockyDataPlanMask | null {
  return catalogResource?.masked
    ? {
        masked: true,
        count: catalogResource.count,
        reason: t("一部の値はプランにより非表示"),
        required_plan: catalogResource.required_plan ?? "—",
      }
    : null;
}

function PageControls({
  canPrevious,
  canNext,
  loading,
  onPrevious,
  onNext,
  count,
  total,
}: {
  canPrevious: boolean;
  canNext: boolean;
  loading: boolean;
  onPrevious: () => void;
  onNext: () => void;
  count: number;
  total: number;
}) {
  return (
    <InlineStack align="space-between" blockAlign="center" gap="200">
      <Text as="p" variant="bodySm" tone="subdued">
        {t("このページ {count} 件 / 取得開始時 {total} 件", { count, total })}
      </Text>
      <InlineStack gap="200">
        <Button disabled={!canPrevious || loading} onClick={onPrevious}>
          {t("前のページ")}
        </Button>
        <Button disabled={!canNext || loading} onClick={onNext}>
          {t("次のページ")}
        </Button>
      </InlineStack>
    </InlineStack>
  );
}

function productIdentityLabel(product: {
  product_title: string;
  variant_title: string | null;
  sku: string | null;
  shopify_variant_id: string;
}): string {
  const title =
    displayVariantTitle(product.product_title, product.variant_title) ??
    product.product_title;
  return t("{title} / SKU: {sku} / Shopify バリアントID: {id}", {
    title,
    sku: product.sku ?? "—",
    id: product.shopify_variant_id,
  });
}

function mismatchReasonLabel(reason: string): string {
  if (reason === "product_absent_from_shopify") {
    return t("元の商品が現在のShopifyにありません");
  }
  if (reason === "variant_absent_from_shopify") {
    return t("元のバリエーションが現在のShopifyにありません");
  }
  return t("保存時の商品と現在のShopify商品が一致しません");
}

function resolutionLabel(
  status: StockyProductMismatch["resolution_status"],
): string {
  if (status === "auto_matched") return t("安全な一致候補あり");
  if (status === "mapped") return t("確認した現在の商品");
  if (status === "not_current") return t("現在の商品には該当しない");
  if (status === "target_stale") return t("以前に確認した商品を再確認してください");
  return t("未解決");
}

function resolutionReasonLabel(record: StockyProductMismatch): string {
  const reason = record.resolution_reason;
  if (reason === "merchant_mapping")
    return t("ストア担当者が現在の商品だと確認しました");
  if (reason === "draft_product_auto_mapping") {
    return t("確認したDRAFT商品を現在の商品として記録しました");
  }
  if (reason === "merchant_marked_not_current") {
    return t("ストア担当者が現在の商品には該当しないと確認しました");
  }
  if (
    reason === "mapped_target_missing" ||
    reason === "mapped_target_deleted"
  ) {
    return t("以前に確認した商品が現在のShopifyにないため、再確認が必要です");
  }
  if (reason.startsWith("ambiguous_")) {
    return t("一致する可能性がある商品が複数あるため、自動では選びません");
  }
  if (reason === "name_only_no_safe_match") {
    return t("商品名だけでは安全に同じ商品と判断できません");
  }
  if (reason === "draft_product_reconciliation_required") {
    return t("同じ下書き商品の作成結果を再確認してください");
  }
  if (reason === "draft_product_mapping_confirmation_required") {
    return t("新しい商品は同期済みです。現在の商品としての確認結果を再読み込みしてください");
  }
  if (reason === "no_current_authoritative_or_unique_sku_match") {
    return t("現在のShopifyに安全に一致するIDまたは一意のSKUがありません");
  }
  if (record.auto_match_basis === "shopify_variant_id") {
    return t("Shopify バリアントIDが一致する現在の商品があります");
  }
  if (record.auto_match_basis === "shopify_inventory_item_id") {
    return t("Shopify在庫アイテムIDが一致する現在の商品があります");
  }
  if (record.auto_match_basis === "unique_exact_sku") {
    return t("同じSKUを持つ現在の商品が1件あります");
  }
  return t("現在の商品として確認できるか判断してください");
}

function MappingRevisionHistory({
  shop,
  sourceVariantId,
}: {
  shop: VerifiedShopGeneration;
  sourceVariantId: string;
}) {
  const revisionsQuery = useQuery({
    queryKey: [
      "stocky-data",
      shop.shopGeneration,
      "product-mapping-revisions",
      sourceVariantId,
    ],
    queryFn: () =>
      listStockyProductMappingRevisions(sourceVariantId, shop.shopDomain),
  });
  if (revisionsQuery.isError) {
    return (
      <QueryError
        error={revisionsQuery.error}
        retry={() => void revisionsQuery.refetch()}
      />
    );
  }
  if (!revisionsQuery.data) return <SkeletonBodyText lines={3} />;
  if (revisionsQuery.data.revisions.length === 0) {
    return (
      <Text as="p" tone="subdued">
        {t("現在の商品に関する変更履歴はありません。")}
      </Text>
    );
  }
  return (
    <BlockStack gap="200">
      {revisionsQuery.data.revisions.map(
        (revision: StockyProductMappingRevision) => (
          <Box
            key={revision.id}
            padding="200"
            background="bg-surface-secondary"
            borderRadius="200"
          >
            <Text as="p">
              {revision.decision === "mapped"
                ? t("revision {revision}: {target} を現在の商品として確認", {
                    revision: revision.revision,
                    target: revision.target_snapshot
                      ? productIdentityLabel(revision.target_snapshot)
                      : t("現在は見つからない商品"),
                  })
                : t("revision {revision}: 現在の商品には該当しない", {
                    revision: revision.revision,
                  })}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {formatDateTime(revision.created_at)}
            </Text>
            {revision.mapping_provenance === "draft_catalog_receipt_v1" && (
              <Text as="p" variant="bodySm" tone="subdued">
                {t("DRAFT商品を現在の商品として確認しました。発注設定・仕入条件・在庫数は未変更です。")}
              </Text>
            )}
          </Box>
        ),
      )}
    </BlockStack>
  );
}

function ProductMismatchCard({
  shop,
  record,
  reload,
  adjustInventory,
  onSaved,
}: {
  shop: VerifiedShopGeneration;
  record: StockyProductMismatch;
  reload: () => void;
  adjustInventory: (shopifyVariantId: string) => void;
  onSaved: (
    response: CreateStockyProductMappingResponse,
    action: "identity_only" | "identity_and_settings" | "settings_only" | "not_current",
  ) => void;
}) {
  const queryClient = useQueryClient();
  const suggestedTarget =
    (record.operational_application_status === "confirmation_required" ||
      record.operational_application_status === "legacy_unapplied" ||
      record.operational_application_status === "source_changed" ||
      record.operational_application_status ===
        "draft_mapping_settings_not_copied") &&
    record.current_target?.current === true
      ? record.current_target
      : null;
  const [searchInput, setSearchInput] = useState(
    suggestedTarget ? productIdentityLabel(suggestedTarget) : "",
  );
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>(
    suggestedTarget ? [String(suggestedTarget.variant_id)] : [],
  );
  const [selectedTarget, setSelectedTarget] =
    useState<StockyCurrentVariant | null>(suggestedTarget);
  const [showHistory, setShowHistory] = useState(false);
  const [conflicted, setConflicted] = useState(false);
  const [draftWorkflowOverride, setDraftWorkflowOverride] =
    useState<StockyMismatchDraftProduct | null>(null);
  const [draftMappingWrittenOverride, setDraftMappingWrittenOverride] =
    useState<boolean | null>(null);
  const [draftNotice, setDraftNotice] = useState<{
    tone: "info" | "success" | "warning" | "critical";
    title: string;
    message: string;
  } | null>(null);
  const [draftCreatePending, setDraftCreatePending] = useState(false);
  const attemptRef = useRef<PersistentIdempotencyAttempt | null>(null);
  const draftCreateInFlight = useRef(false);
  const draftEditInFlight = useRef(false);
  const draftCatalogSyncInFlight = useRef(false);
  const draftLegacyUpgradeInFlight = useRef(false);

  useEffect(() => {
    if (selectedTarget !== null) return;
    const timer = window.setTimeout(
      () => setDebouncedSearch(searchInput.trim()),
      300,
    );
    return () => window.clearTimeout(timer);
  }, [searchInput, selectedTarget]);

  const variantsQuery = useQuery({
    queryKey: [
      "stocky-data",
      shop.shopGeneration,
      "current-variants",
      debouncedSearch,
    ],
    queryFn: () =>
      searchStockyCurrentVariants(debouncedSearch, shop.shopDomain, 20),
    enabled: debouncedSearch.length > 0,
  });

  const mutation = useMutation({
    mutationKey: [
      "stocky-data",
      shop.shopGeneration,
      "product-mapping",
      record.source_variant_id,
    ],
    mutationFn: (variables: {
      input: {
        decision: "mapped" | "not_current";
        target_variant_id: string | null;
        expected_revision: number;
      };
      attempt: PersistentIdempotencyAttempt;
      action:
        | "identity_only"
        | "identity_and_settings"
        | "settings_only"
        | "not_current";
    }) =>
      createStockyProductMappingRevision(
        record.source_variant_id,
        variables.input,
        variables.attempt.key,
        shop.shopDomain,
      ),
    onSuccess: (response, variables) => {
      clearPersistentIdempotencyAttempt(
        `stocky-product-mapping:${record.source_variant_id}`,
        variables.attempt,
      );
      attemptRef.current = null;
      setConflicted(false);
      if (response.inventory_written === false) onSaved(response, variables.action);
      void queryClient.invalidateQueries({
        queryKey: ["stocky-data", shop.shopGeneration],
      });
    },
    onError: (error, variables) => {
      if (
        error instanceof ApiError &&
        error.status >= 400 &&
        error.status < 500
      ) {
        clearPersistentIdempotencyAttempt(
          `stocky-product-mapping:${record.source_variant_id}`,
          variables.attempt,
        );
        attemptRef.current = null;
      }
      if (
        error instanceof ApiError &&
        error.status === 409 &&
        error.code !== "stocky_product_operational_settings_conflict" &&
        error.code !== "stocky_data_contract_reload_required"
      ) {
        setConflicted(true);
        void queryClient.invalidateQueries({
          queryKey: ["stocky-data", shop.shopGeneration],
        });
      }
    },
  });

  const draftRequest = {
    source: record.source,
    source_snapshot_revision: record.source_snapshot_revision,
    expected_mapping_revision: record.latest_revision?.revision ?? 0,
  };
  const draftCreateMutation = useMutation({
    mutationKey: [
      "stocky-data",
      shop.shopGeneration,
      "product-mismatch-draft",
      record.source_variant_id,
    ],
    mutationFn: () =>
      createOrReconcileStockyMismatchDraftProduct(
        record.source_variant_id,
        draftRequest,
        shop.shopDomain,
      ),
    onSuccess: (response) => {
      setDraftWorkflowOverride(response.draft_product);
      setDraftMappingWrittenOverride(response.mapping_written);
      void queryClient.invalidateQueries({
        queryKey: ["stocky-data", shop.shopGeneration],
      });
    },
    onError: () => {
      // A lost response may still have created the server-owned claim or
      // Shopify DRAFT. Refresh the exact row so recovery never creates a
      // second product from browser-only assumptions.
      void queryClient.invalidateQueries({
        queryKey: ["stocky-data", shop.shopGeneration],
      });
    },
  });
  const draftCatalogSyncMutation = useMutation({
    mutationKey: [
      "stocky-data",
      shop.shopGeneration,
      "product-mismatch-draft-catalog-sync",
      record.source_variant_id,
    ],
    mutationFn: (claimToken: string) =>
      syncStockyMismatchDraftProductToCatalog(
        record.source_variant_id,
        { ...draftRequest, claim_token: claimToken },
        shop.shopDomain,
      ),
    onSuccess: (response) => {
      setDraftWorkflowOverride(response.draft_product);
      setDraftMappingWrittenOverride(response.mapping_written);
      void queryClient.invalidateQueries({
        queryKey: ["stocky-data", shop.shopGeneration],
      });
    },
    onError: () => {
      void queryClient.invalidateQueries({
        queryKey: ["stocky-data", shop.shopGeneration],
      });
    },
  });
  const draftLegacyUpgradeMutation = useMutation({
    mutationKey: [
      "stocky-data",
      shop.shopGeneration,
      "product-mismatch-draft-legacy-upgrade",
      record.source_variant_id,
    ],
    mutationFn: (claimToken: string) =>
      completeLegacyStockyMismatchDraftCatalogMapping(
        record.source_variant_id,
        { ...draftRequest, claim_token: claimToken },
        shop.shopDomain,
      ),
    onSuccess: (response) => {
      setDraftWorkflowOverride(response.draft_product);
      setDraftMappingWrittenOverride(response.mapping_written);
      void queryClient.invalidateQueries({
        queryKey: ["stocky-data", shop.shopGeneration],
      });
    },
    onError: () => {
      void queryClient.invalidateQueries({
        queryKey: ["stocky-data", shop.shopGeneration],
      });
    },
  });

  const draftWorkflow = draftWorkflowOverride ?? record.draft_product;
  const draftMappingWritten =
    draftMappingWrittenOverride ??
    record.latest_revision?.mapping_provenance === "draft_catalog_receipt_v1";
  const exactDraftTarget =
    draftWorkflow?.state === "catalog_synced"
      ? draftWorkflow.current_target
      : null;
  const draftIdentityConfirmedThroughProductDecision =
    exactDraftTarget !== null &&
    record.latest_revision?.decision === "mapped" &&
    record.latest_revision.target_variant_id === exactDraftTarget.variant_id &&
    record.current_target?.current === true &&
    record.current_target.variant_id === exactDraftTarget.variant_id &&
    record.current_target.shopify_product_id ===
      exactDraftTarget.shopify_product_id &&
    record.current_target.shopify_variant_id ===
      exactDraftTarget.shopify_variant_id;
  const draftProductIdentityConfirmed =
    draftMappingWritten || draftIdentityConfirmedThroughProductDecision;
  const draftOperationalSettingsApplied =
    draftIdentityConfirmedThroughProductDecision &&
    (record.latest_revision?.operational_application_version === 1 ||
      record.operational_application_status === "applied");
  const hasOperationalSettingsToReview =
    record.operational_application_status === "confirmation_required" ||
    record.operational_application_status === "legacy_unapplied" ||
    record.operational_application_status === "source_changed" ||
    record.operational_application_status === "mapping_required" ||
    record.operational_application_status ===
      "draft_mapping_settings_not_copied";
  const currentProductIdentityAlreadyConfirmed =
    record.latest_revision?.decision === "mapped" &&
    record.latest_revision.target_variant_id !== null &&
    record.current_target?.current === true &&
    record.latest_revision.target_variant_id === record.current_target.variant_id;
  const exactDraftProductIdentityAlreadyConfirmed =
    draftMappingWritten &&
    exactDraftTarget !== null &&
    exactDraftTarget.current === true &&
    record.current_target?.current === true &&
    exactDraftTarget.variant_id === record.current_target.variant_id &&
    exactDraftTarget.shopify_product_id ===
      record.current_target.shopify_product_id &&
    exactDraftTarget.shopify_variant_id ===
      record.current_target.shopify_variant_id;
  const selectedProductIdentityAlreadyConfirmed =
    selectedTarget !== null &&
    (currentProductIdentityAlreadyConfirmed ||
      exactDraftProductIdentityAlreadyConfirmed) &&
    selectedTarget.variant_id === record.current_target?.variant_id;
  const settingsToApplySummary = t(
    "反映するもの: 発注設定 {reorderPolicies}件、商品と仕入先の条件 {supplierTerms}件。",
    {
      reorderPolicies: record.related_counts.reorder_policies,
      supplierTerms: record.related_counts.variant_supplier_conditions,
    },
  );
  const operationalSettingsUnchanged = t(
    "在庫数、税率、棚卸し・在庫調整の履歴、既存の発注書、BOM、保存済み原本は変更しません。",
  );
  const mappedActionLabel = selectedProductIdentityAlreadyConfirmed
    ? t("発注設定と仕入条件を確認して反映")
    : hasOperationalSettingsToReview
      ? t("現在の商品として確認し、発注設定と仕入条件を反映")
      : t("この商品を現在の商品として確認する");

  const openDraftEditor = async (workflow: StockyMismatchDraftProduct) => {
    if (
      draftEditInFlight.current ||
      workflow.shopify_product_id === null ||
      workflow.state === "not_started" ||
      workflow.state === "creating"
    ) {
      return;
    }
    draftEditInFlight.current = true;
    setDraftNotice(null);
    try {
      assertCurrentShopGeneration(shop.shopDomain);
      const outcome = await editShopifyProductWithAdminIntent(
        workflow.shopify_product_id,
      );
      assertCurrentShopGeneration(shop.shopDomain);
      setDraftNotice(
        outcome === "closed"
          ? {
              tone: "info",
              title: t("Shopifyの商品編集を閉じました"),
              message: t(
                "下書き商品はそのまま残っています。重複作成せず、同じ商品の編集を再開できます。",
              ),
            }
          : {
              tone: "info",
              title: t("Shopifyでの確認内容はまだ自動反映していません"),
              message: t(
                "Shopifyで保存した内容をUreyukiBoxの商品一覧へ同期し、現在の商品として記録するには、「確認して同期し、現在の商品として記録」を押してください。発注設定・仕入条件・在庫数は変更しません。",
              ),
            },
      );
    } catch (error) {
      if (error instanceof ShopGenerationChangedError) return;
      setDraftNotice({
        tone: "critical",
        title: t("Shopifyの商品編集を開けませんでした"),
        message:
          error instanceof Error
            ? error.message
            : t("同じ下書き商品の編集をもう一度開いてください。"),
      });
    } finally {
      draftEditInFlight.current = false;
    }
  };

  const createOrResumeDraft = async () => {
    if (
      draftCreateInFlight.current ||
      draftCatalogSyncInFlight.current ||
      mutation.isPending
    ) {
      return;
    }
    if (
      draftWorkflow === null &&
      !window.confirm(
        t(
          "保存済みの「{title}」の商品名とSKUを初期値にして、ShopifyへDRAFT商品を1件作成します。在庫数、発注設定、仕入条件、BOMはコピーしません。Shopifyで内容を確認した後、正確な商品を同期して現在の商品として記録します。よろしいですか？",
          {
            title:
              displayVariantTitle(
                record.source.product_title,
                record.source.variant_title,
              ) ?? record.source.product_title,
          },
        ),
      )
    ) {
      return;
    }
    draftCreateInFlight.current = true;
    setDraftCreatePending(true);
    setDraftNotice(null);
    try {
      assertCurrentShopGeneration(shop.shopDomain);
      await ensureShopifyOptionalScope("write_products");
      assertCurrentShopGeneration(shop.shopDomain);
      const response = await draftCreateMutation.mutateAsync();
      assertCurrentShopGeneration(shop.shopDomain);
      const workflow = response.draft_product;
      if (workflow.state === "creating") {
        setDraftNotice({
          tone: "warning",
          title: t("下書き商品の作成結果を確認しています"),
          message: t(
            "同じ作成記録だけを再確認します。新しい商品は作らず、少し待ってから「作成結果を再確認」を押してください。",
          ),
        });
        return;
      }
      if (workflow.state === "not_started") {
        setDraftNotice({
          tone: "warning",
          title: t("下書き商品の作成を開始できませんでした"),
          message: t(
            "同じ作成記録を保持しています。権限と通信を確認してから再試行してください。",
          ),
        });
        return;
      }
      if (workflow.state === "ready") {
        await openDraftEditor(workflow);
      }
    } catch (error) {
      if (error instanceof ShopGenerationChangedError) return;
      setDraftNotice({
        tone: "critical",
        title: t("Shopifyの下書き商品を作成・確認できませんでした"),
        message:
          error instanceof ShopifyOptionalScopeError
            ? t(
                "Shopifyの商品編集権限を確認できませんでした。権限を許可してから同じ処理を再試行してください。",
              )
            : error instanceof ApiError
              ? error.detail
            : error instanceof Error
              ? error.message
              : t("同じ作成記録を使ってもう一度お試しください。"),
      });
    } finally {
      draftCreateInFlight.current = false;
      setDraftCreatePending(false);
    }
  };

  const syncExactDraftToCatalog = async () => {
    if (
      draftWorkflow === null ||
      draftWorkflow.state !== "ready" ||
      draftCatalogSyncInFlight.current ||
      draftCreateInFlight.current ||
      mutation.isPending
    ) {
      return;
    }
    if (
      !window.confirm(
        t(
          "ShopifyでDRAFT商品の内容を保存したことを確認し、その商品とバリエーションをUreyukiBoxの商品一覧へ同期して、保存商品に対する現在の商品として記録します。発注設定、仕入条件、在庫数、BOMは変更しません。よろしいですか？",
        ),
      )
    ) {
      return;
    }
    draftCatalogSyncInFlight.current = true;
    setDraftNotice(null);
    try {
      assertCurrentShopGeneration(shop.shopDomain);
      const response = await draftCatalogSyncMutation.mutateAsync(
        draftWorkflow.claim_token,
      );
      assertCurrentShopGeneration(shop.shopDomain);
      if (
        response.draft_product.state !== "catalog_synced" ||
        response.draft_product.current_target === null ||
        response.mapping_written !== true
      ) {
        throw new Error("exact draft catalog sync was not confirmed");
      }
      setDraftNotice({
        tone: "success",
        title: t("新しいDRAFT商品を同期し、現在の商品として確認しました"),
        message: t(
          "このDRAFT商品を現在の商品として確認しました。発注設定、仕入条件、在庫数、BOMは変更していません。設定や在庫の反映は内容を確認して別に実行してください。",
        ),
      });
    } catch (error) {
      if (error instanceof ShopGenerationChangedError) return;
      setDraftNotice({
        tone: "critical",
        title: t("新しい商品を同期できませんでした"),
        message:
          error instanceof ApiError
            ? error.detail
            : error instanceof Error
            ? error.message
            : t("下書き商品は残っています。内容を確認してから同じ同期を再試行してください。"),
      });
    } finally {
      draftCatalogSyncInFlight.current = false;
    }
  };

  const completeLegacyDraftMapping = async () => {
    if (
      draftWorkflow === null ||
      draftWorkflow.state !== "catalog_synced" ||
      !draftWorkflow.legacy_mapping_upgrade_available ||
      draftProductIdentityConfirmed ||
      draftLegacyUpgradeInFlight.current ||
      draftCreateInFlight.current ||
      draftCatalogSyncInFlight.current ||
      mutation.isPending
    ) {
      return;
    }
    if (
      !window.confirm(
        t(
          "以前に作成・同期したこのDRAFT商品の正確なバリエーションを、現在の商品として確認します。Shopifyの商品作成・検索・選択は行わず、発注設定、仕入条件、在庫数、BOMも変更しません。よろしいですか？",
        ),
      )
    ) {
      return;
    }
    draftLegacyUpgradeInFlight.current = true;
    setDraftNotice(null);
    try {
      assertCurrentShopGeneration(shop.shopDomain);
      const response = await draftLegacyUpgradeMutation.mutateAsync(
        draftWorkflow.claim_token,
      );
      assertCurrentShopGeneration(shop.shopDomain);
      if (
        response.draft_product.state !== "catalog_synced" ||
        response.draft_product.current_target === null ||
        response.mapping_written !== true
      ) {
        throw new Error("legacy draft mapping was not confirmed");
      }
      setDraftNotice({
        tone: "success",
        title: t("作成済みDRAFT商品を現在の商品として確認しました"),
        message: t(
          "保存商品と現在の商品が同じだと確認しました。発注設定、仕入条件、在庫数、BOMは変更していません。設定や在庫の反映は内容を確認して別に実行してください。",
        ),
      });
    } catch (error) {
      if (error instanceof ShopGenerationChangedError) return;
      setDraftNotice({
        tone: "critical",
        title: t("作成済みDRAFT商品を現在の商品として確認できませんでした"),
        message:
          error instanceof ApiError
            ? error.detail
            : error instanceof Error
              ? error.message
              : t("表示中の商品情報を更新してから、もう一度お試しください。"),
      });
    } finally {
      draftLegacyUpgradeInFlight.current = false;
    }
  };

  const draftSourceLocked =
    draftCreatePending ||
    draftCreateMutation.isPending ||
    draftCatalogSyncMutation.isPending ||
    draftLegacyUpgradeMutation.isPending;

  const submitDecision = (
    decision: "mapped" | "not_current",
    target: StockyCurrentVariant | null,
  ) => {
    if (draftSourceLocked) return;
    const expectedRevision = record.latest_revision?.revision ?? 0;
    const input = {
      decision,
      target_variant_id:
        decision === "mapped" ? (target?.variant_id ?? null) : null,
      expected_revision: expectedRevision,
    };
    if (decision === "mapped" && input.target_variant_id === null) return;
    const action =
      decision === "not_current"
        ? "not_current"
        : hasOperationalSettingsToReview
          ? selectedProductIdentityAlreadyConfirmed
            ? "settings_only"
            : "identity_and_settings"
          : "identity_only";
    const confirmed = window.confirm(
      decision === "mapped"
        ? action === "settings_only"
          ? t(
              "「{target}」は現在の商品としてすでに確認済みです。発注設定と仕入条件を確認して反映します。{settings} 現在の設定と異なる場合は何も保存せず停止します。{unchanged} よろしいですか？",
              {
                target: target ? productIdentityLabel(target) : "",
                settings: settingsToApplySummary,
                unchanged: operationalSettingsUnchanged,
              },
            )
          : action === "identity_and_settings"
            ? t(
                "保存商品と「{target}」が同じ商品だと確認し、発注設定と仕入条件を確認して反映します。{settings} 現在の設定と異なる場合は何も保存せず停止します。{unchanged} よろしいですか？",
                {
                  target: target ? productIdentityLabel(target) : "",
                  settings: settingsToApplySummary,
                  unchanged: operationalSettingsUnchanged,
                },
              )
            : t(
                "保存商品と「{target}」が同じ商品だと確認します。在庫数、税率、棚卸し・在庫調整の履歴、既存の発注書、BOM、保存済み原本は変更しません。よろしいですか？",
                { target: target ? productIdentityLabel(target) : "" },
              )
        : t(
            "この保存データは現在の商品には該当しないものとして記録します。発注設定・仕入条件、在庫数、税率、棚卸し・在庫調整の履歴、既存の発注書、BOM、保存済み原本は変更しません。よろしいですか？",
          ),
    );
    if (!confirmed) return;
    const fingerprint = JSON.stringify(input);
    const attempt = persistentIdempotencyAttempt({
      scope: `stocky-product-mapping:${record.source_variant_id}`,
      shopGeneration: shop.shopGeneration,
      previous: attemptRef.current,
      fingerprint,
    });
    attemptRef.current = attempt;
    setConflicted(false);
    mutation.mutate({ input, attempt, action });
  };

  const options = (variantsQuery.data?.variants ?? [])
    .filter((variant) => variant.current)
    .map((variant) => ({
      value: String(variant.variant_id),
      label: productIdentityLabel(variant),
    }));
  const target = record.current_target;
  const canAdjust = target?.current === true;

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="start" gap="300" wrap>
          <BlockStack gap="100">
            <Text as="h3" variant="headingMd">
              {displayVariantTitle(
                record.source.product_title,
                record.source.variant_title,
              ) ?? record.source.product_title}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {t(
                "SKU: {sku}・Shopify商品ID: {productId}・バリアントID: {variantId}・在庫アイテムID: {inventoryItemId}",
                {
                  sku: record.source.sku ?? "—",
                  productId: record.source.shopify_product_id,
                  variantId: record.source.shopify_variant_id,
                  inventoryItemId:
                    record.source.shopify_inventory_item_id ?? "—",
                },
              )}
            </Text>
          </BlockStack>
          <Badge
            tone={
              record.operational_application_status ===
                "confirmation_required" ||
              record.operational_application_status === "legacy_unapplied" ||
              record.operational_application_status === "source_changed" ||
              record.operational_application_status === "mapping_required" ||
              record.operational_application_status ===
                "draft_mapping_settings_not_copied"
                ? "attention"
                : record.resolution_status === "mapped" ||
                    record.resolution_status === "auto_matched"
                ? "success"
                : record.resolution_status === "target_stale"
                  ? "critical"
                  : "attention"
            }
          >
            {resolutionLabel(record.resolution_status)}
          </Badge>
        </InlineStack>

        <BlockStack gap="100">
          {record.mismatch_reasons.map((reason) => (
            <Text key={reason} as="p">
              {mismatchReasonLabel(reason)}
            </Text>
          ))}
          <Text as="p" tone="subdued">
            {resolutionReasonLabel(record)}
          </Text>
        </BlockStack>

        <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
          <Box
            padding="300"
            background="bg-surface-secondary"
            borderRadius="200"
          >
            <BlockStack gap="100">
              <Text as="h4" variant="headingSm">
                {t("関連する保存データ")}
              </Text>
              <Text as="p">
                {t("商品と仕入先の条件 {count}件", {
                  count: record.related_counts.variant_supplier_conditions,
                })}
              </Text>
              <Text as="p">
                {t("発注設定 {count}件", {
                  count: record.related_counts.reorder_policies,
                })}
              </Text>
              <Text as="p">
                {t("棚卸し明細 {count}件", {
                  count: record.related_counts.stocktake_items,
                })}
              </Text>
            </BlockStack>
          </Box>
          <Box
            padding="300"
            background="bg-surface-secondary"
            borderRadius="200"
          >
            <BlockStack gap="150">
              <Text as="h4" variant="headingSm">
                {t("確認した現在の商品")}
              </Text>
              {target ? (
                <>
                  <Text as="p">{productIdentityLabel(target)}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {target.current
                      ? target.orderable
                        ? t("現在のShopify商品・発注可能")
                        : t("現在のShopify商品・発注対象外")
                      : t("以前に確認した商品は現在のShopifyにありません")}
                  </Text>
                  {canAdjust && (
                    <Button
                      variant="plain"
                      onClick={() => adjustInventory(target.shopify_variant_id)}
                    >
                      {t("在庫数を調整")}
                    </Button>
                  )}
                </>
              ) : (
                <Text as="p" tone="subdued">
                  {record.resolution_status === "not_current"
                    ? t("現在の商品には該当しないと記録済みです。")
                    : t("現在の商品はまだ選ばれていません。")}
                </Text>
              )}
            </BlockStack>
          </Box>
        </InlineGrid>

        {record.resolution_status === "target_stale" && (
          <Banner tone="critical" title={t("以前に確認した商品が見つかりません")}>
            <p>
              {t(
                "現在の商品を再検索して、同じ商品か確認し直してください。在庫数は自動では変更しません。",
              )}
            </p>
          </Banner>
        )}
        {hasOperationalSettingsToReview && (
          <Banner
            tone="warning"
            title={t("発注設定と仕入条件の反映には確認が必要です")}
          >
            <BlockStack gap="100">
              <p>
                {selectedProductIdentityAlreadyConfirmed
                  ? record.operational_application_status ===
                    "draft_mapping_settings_not_copied"
                    ? t(
                        "このDRAFT商品の正確なバリエーションは、現在の商品としてすでに確認済みです。発注設定と仕入条件はまだ反映していません。表示中の商品と設定を確認して、「発注設定と仕入条件を確認して反映」を押してください。",
                      )
                    : record.operational_application_status === "source_changed"
                      ? t(
                          "現在の商品としての確認は済んでいますが、以前の反映後に保存元の発注設定または仕入条件が変わりました。自動では上書きせず、Stockyから消えた設定もUreyukiBoxから自動削除しません。表示中の設定を確認して、「発注設定と仕入条件を確認して反映」を押してください。",
                        )
                      : t(
                          "現在の商品としての確認は済んでいます。発注設定と仕入条件はまだ反映していません。表示中の設定を確認して、「発注設定と仕入条件を確認して反映」を押してください。",
                        )
                  : record.operational_application_status === "mapping_required"
                    ? t(
                        "旧商品に発注設定または仕入条件があります。現在の商品はまだ確認されていません。候補を選び、同じ商品か確認したうえで「現在の商品として確認し、発注設定と仕入条件を反映」を押してください。",
                      )
                    : t(
                        "安全な一致候補はありますが、現在の商品としてはまだ確認されていません。候補と設定を確認して、「現在の商品として確認し、発注設定と仕入条件を反映」を押してください。",
                      )}
              </p>
              <p>{settingsToApplySummary}</p>
              <p>{operationalSettingsUnchanged}</p>
            </BlockStack>
          </Banner>
        )}
        {conflicted && (
          <Banner
            tone="warning"
            title={t("現在の商品に関する確認が別の画面で更新されました")}
            action={{ content: t("最新状態を再読み込み"), onAction: reload }}
          >
            <p>{t("最新状態を確認してから、もう一度選択してください。")}</p>
          </Banner>
        )}
        {mutation.isError && !conflicted && (
          <Banner
            tone="critical"
            title={
              selectedProductIdentityAlreadyConfirmed
                ? t("発注設定と仕入条件を反映できませんでした")
                : t("現在の商品として確認した結果を保存できませんでした")
            }
          >
            <p>{errorMessage(mutation.error)}</p>
            <p>
              {t(
                "通信が途切れた場合は、同じ内容でもう一度押してください。二重には記録しません。",
              )}
            </p>
          </Banner>
        )}

        <Divider />
        <BlockStack gap="300">
          <BlockStack gap="100">
            <Text as="h4" variant="headingSm">
              {t("この保存商品をもとに新しいShopify商品を作る")}
            </Text>
            <Text as="p" tone="subdued">
              {t(
          "保存済みの商品名とSKUだけを初期値にしたDRAFT商品を作成します。確認後、このDRAFTの正確なバリエーションを現在の商品として自動で記録します。在庫数、発注設定、仕入条件、BOMはコピーも変更もしません。",
              )}
            </Text>
          </BlockStack>

          {draftNotice !== null && (
            <Banner tone={draftNotice.tone} title={draftNotice.title}>
              <p>{draftNotice.message}</p>
            </Banner>
          )}

          {draftWorkflow === null ? (
            <InlineStack gap="200" wrap>
              <Button
                onClick={() => void createOrResumeDraft()}
                disabled={
                  mutation.isPending ||
                  draftCatalogSyncMutation.isPending ||
                  draftLegacyUpgradeMutation.isPending
                }
                loading={draftCreatePending}
              >
                {t("Shopifyに下書き商品を作成")}
              </Button>
            </InlineStack>
          ) : draftWorkflow.state === "not_started" ||
            draftWorkflow.state === "creating" ? (
            <BlockStack gap="200">
              <Banner
                tone="warning"
                title={
                  draftWorkflow.state === "creating"
                    ? t("下書き商品の作成結果を確認しています")
                    : t("下書き商品の作成を再開できます")
                }
              >
                <p>
                  {t(
                    "サーバーに保存した同じ作成記録を再確認します。別の商品は作成しません。",
                  )}
                </p>
              </Banner>
              <InlineStack gap="200" wrap>
                <Button
                  onClick={() => void createOrResumeDraft()}
                  disabled={
                    mutation.isPending ||
                    draftCatalogSyncMutation.isPending ||
                    draftLegacyUpgradeMutation.isPending
                  }
                  loading={draftCreatePending}
                >
                  {t("作成結果を再確認")}
                </Button>
              </InlineStack>
            </BlockStack>
          ) : draftWorkflow.state === "ready" ? (
            <BlockStack gap="200">
              <Banner tone="info" title={t("Shopifyに下書き商品を保存しています")}>
                <p>
                  {t(
                    "同じDRAFT商品をShopifyで確認・保存してください。次のボタンで正確な商品を同期し、この保存商品に対する現在の商品として自動で記録します。発注設定、仕入条件、在庫数、BOMは変わりません。",
                  )}
                </p>
              </Banner>
              <InlineStack gap="200" wrap>
                <Button
                  onClick={() => void openDraftEditor(draftWorkflow)}
                  disabled={
                    draftEditInFlight.current ||
                    draftCreateMutation.isPending ||
                    draftCatalogSyncMutation.isPending ||
                    draftLegacyUpgradeMutation.isPending
                  }
                >
                  {t("Shopifyで下書き商品を確認")}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => void syncExactDraftToCatalog()}
                  disabled={draftCreateMutation.isPending || mutation.isPending}
                  loading={draftCatalogSyncMutation.isPending}
                >
                  {t("確認して同期し、現在の商品として記録")}
                </Button>
              </InlineStack>
              <Text as="p" variant="bodySm" tone="subdued">
                {t(
                  "この操作はDRAFT商品とバリエーションをUreyukiBoxへ同期し、保存商品に対する現在の商品として自動で記録します。在庫数量、発注設定、仕入条件、BOMはコピーも変更もしません。",
                )}
              </Text>
            </BlockStack>
          ) : (
            <Banner tone="success" title={t("新しいDRAFT商品はUreyukiBoxに同期済みです")}>
              <BlockStack gap="150">
                {draftWorkflow.current_target !== null && (
                  <Text as="p">
                    {productIdentityLabel(draftWorkflow.current_target)}
                  </Text>
                )}
                <Text as="p">
                  {draftMappingWritten
                    ? t(
                        "このDRAFT商品の正確なバリエーションを現在の商品として確認しました。発注設定、仕入条件、在庫数、BOMは変更していません。必要なら下で商品を確認し、設定反映は明示的に実行してください。",
                      )
                    : draftIdentityConfirmedThroughProductDecision
                      ? draftOperationalSettingsApplied
                        ? t(
                            "通常の確認操作で、保存商品とこのDRAFT商品が同じだと確認済みです。発注設定と仕入条件も反映しました。在庫数・BOMは変更していません。",
                          )
                        : t(
                            "通常の確認操作で、保存商品とこのDRAFT商品が同じだと確認済みです。発注設定と仕入条件はまだ反映していません。在庫数・BOMも変更していません。",
                          )
                    : draftWorkflow.legacy_mapping_upgrade_available
                      ? t(
                          "この作成済み商品は従来の確認で同期済みですが、当時の説明どおり現在の商品としてはまだ記録していません。下の明示操作では、このレシートの正確な商品だけを現在の商品として確認します。発注設定、仕入条件、在庫数、BOMは変更しません。",
                        )
                      : t(
                          "この保存商品の現在の商品としての確認状態を取得できません。画面を再読み込みしてから、現在の商品を確認してください。",
                        )}
                </Text>
                <InlineStack gap="200" wrap>
                  {!draftProductIdentityConfirmed &&
                    draftWorkflow.legacy_mapping_upgrade_available && (
                      <Button
                        variant="primary"
                        onClick={() => void completeLegacyDraftMapping()}
                        disabled={
                          mutation.isPending ||
                          draftCreateMutation.isPending ||
                          draftCatalogSyncMutation.isPending
                        }
                        loading={draftLegacyUpgradeMutation.isPending}
                      >
                        {t("この作成済み商品を現在の商品として確認")}
                      </Button>
                    )}
                  <Button
                    variant="plain"
                    onClick={() => void openDraftEditor(draftWorkflow)}
                    disabled={draftLegacyUpgradeMutation.isPending}
                  >
                    {t("Shopifyで商品をもう一度確認")}
                  </Button>
                </InlineStack>
              </BlockStack>
            </Banner>
          )}
        </BlockStack>

        <Divider />
        <BlockStack gap="300">
          <Text as="h4" variant="headingSm">
            {t("現在の商品を選ぶ")}
          </Text>
          <Autocomplete
            options={options}
            selected={selectedIds}
            onSelect={(ids) => {
              const selectedId = ids[0];
              const candidate = variantsQuery.data?.variants.find(
                (variant) => String(variant.variant_id) === selectedId,
              );
              setSelectedIds(candidate ? [String(candidate.variant_id)] : []);
              setSelectedTarget(candidate ?? null);
              if (candidate) setSearchInput(productIdentityLabel(candidate));
            }}
            listTitle={t("現在のShopify商品候補")}
            loading={variantsQuery.isFetching}
            emptyState={
              <Text as="p" tone="subdued">
                {debouncedSearch
                  ? t("一致する現在の商品はありません。")
                  : t("商品名・SKU・Shopify バリアントIDで検索してください。")}
              </Text>
            }
            textField={
              <Autocomplete.TextField
                label={t("現在のShopify商品を検索")}
                value={searchInput}
                disabled={draftSourceLocked}
                onChange={(value) => {
                  setSearchInput(value);
                  setSelectedIds([]);
                  setSelectedTarget(null);
                }}
                placeholder={t("商品名・SKU・Shopify バリアントID")}
                autoComplete="off"
                clearButton
                onClearButtonClick={() => {
                  setSearchInput("");
                  setSelectedIds([]);
                  setSelectedTarget(null);
                }}
              />
            }
          />
          {variantsQuery.isError && (
            <QueryError
              error={variantsQuery.error}
              retry={() => void variantsQuery.refetch()}
            />
          )}
          <InlineStack gap="200" wrap>
            <Button
              variant="primary"
              disabled={
                selectedTarget === null || mutation.isPending || draftSourceLocked
              }
              loading={
                mutation.isPending &&
                mutation.variables?.input.decision === "mapped"
              }
              onClick={() => submitDecision("mapped", selectedTarget)}
            >
              {mappedActionLabel}
            </Button>
            <Button
              disabled={mutation.isPending || draftSourceLocked}
              loading={
                mutation.isPending &&
                mutation.variables?.input.decision === "not_current"
              }
              onClick={() => submitDecision("not_current", null)}
            >
              {t("現在の商品には該当しない")}
            </Button>
          </InlineStack>
          <Text as="p" variant="bodySm" tone="subdued">
            {selectedProductIdentityAlreadyConfirmed
              ? t(
                  "現在の商品としての確認は済んでいます。発注設定と仕入条件を確認して反映します。異なる現在の設定は上書きしません。{settings} {unchanged}",
                  {
                    settings: settingsToApplySummary,
                    unchanged: operationalSettingsUnchanged,
                  },
                )
              : hasOperationalSettingsToReview
                ? t(
                    "現在の商品としての確認と、発注設定・仕入条件の反映を同じ操作で行います。異なる現在の設定は上書きしません。{settings} {unchanged}",
                    {
                      settings: settingsToApplySummary,
                      unchanged: operationalSettingsUnchanged,
                    },
                  )
                : t(
                    "この商品を現在の商品として確認します。在庫数、税率、棚卸し・在庫調整の履歴、既存の発注書、BOM、保存済み原本は変更しません。",
                  )}
          </Text>
        </BlockStack>

        <Divider />
        <BlockStack gap="200">
          <Button
            variant="plain"
            onClick={() => setShowHistory((value) => !value)}
          >
            {showHistory
              ? t("現在の商品に関する変更履歴を閉じる")
              : t("現在の商品に関する変更履歴を見る")}
          </Button>
          {showHistory && (
            <MappingRevisionHistory
              shop={shop}
              sourceVariantId={record.source_variant_id}
            />
          )}
        </BlockStack>
      </BlockStack>
    </Card>
  );
}

function ProductMismatchList({
  shop,
  adjustInventory,
}: {
  shop: VerifiedShopGeneration;
  adjustInventory: (shopifyVariantId: string) => void;
}) {
  const [draftQuery, setDraftQuery] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"pending" | "resolved">("pending");
  const [afterIds, setAfterIds] = useState<Array<string | null>>([null]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [savedDecisionResolved, setSavedDecisionResolved] = useState(false);
  const afterId = afterIds[afterIds.length - 1];
  useEffect(() => {
    const normalized = draftQuery.trim();
    if (normalized === query) return;
    const timer = window.setTimeout(() => {
      setQuery(normalized);
      setAfterIds([null]);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [draftQuery, query]);
  const mismatchQuery = useQuery({
    queryKey: [
      "stocky-data",
      shop.shopGeneration,
      "product-mismatches",
      status,
      query,
      afterId,
    ],
    queryFn: () =>
      listStockyProductMismatches(
        { q: query, afterId, limit: 50, status },
        shop.shopDomain,
      ),
  });
  const applySearch = () => {
    setQuery(draftQuery.trim());
    setAfterIds([null]);
  };
  const reload = () => void mismatchQuery.refetch();
  const selectStatus = (next: "pending" | "resolved") => {
    setStatus(next);
    setDraftQuery("");
    setQuery("");
    setAfterIds([null]);
    setSuccessMessage(null);
    setSavedDecisionResolved(false);
  };

  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="300">
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">
              {t("商品不整合")}
            </Text>
            <Text as="p" tone="subdued">
              {t("保存データと現在のShopify商品が一致しない項目を確認します。")}
            </Text>
          </BlockStack>
          <Banner
            tone="info"
            title={t("現在の商品としての確認と設定反映を分けて表示します")}
          >
            <p>
              {t(
                "現在の商品としての確認が済んでいる場合は、発注設定と仕入条件の反映だけを明示的に実行できます。反映するのは旧商品の発注設定と仕入条件だけで、異なる現在の設定は上書きしません。税率、在庫数、棚卸し・在庫調整の履歴、既存の発注書、BOM、保存済み原本は変更しません。",
              )}
            </p>
          </Banner>
          <InlineStack gap="200" blockAlign="end" wrap>
            <Box minWidth="280px">
              <TextField
                label={t("商品不整合を検索")}
                value={draftQuery}
                onChange={setDraftQuery}
                autoComplete="off"
                placeholder={t("元または現在の商品名・SKU・商品ID")}
                helpText={t(
                  "元の商品と、候補・確認した現在商品の両方を検索します。",
                )}
                clearButton
                onClearButtonClick={() => {
                  setDraftQuery("");
                  setQuery("");
                  setAfterIds([null]);
                }}
                maxLength={200}
              />
            </Box>
            <Button onClick={applySearch}>{t("検索")}</Button>
          </InlineStack>
          <InlineStack gap="200" wrap>
            <Button
              variant={status === "pending" ? "primary" : "secondary"}
              onClick={() => selectStatus("pending")}
            >
              {t("要確認（{count}）", {
                count: mismatchQuery.data?.counts.pending ?? 0,
              })}
            </Button>
            <Button
              variant={status === "resolved" ? "primary" : "secondary"}
              onClick={() => selectStatus("resolved")}
            >
              {t("解決済み（{count}）", {
                count: mismatchQuery.data?.counts.resolved ?? 0,
              })}
            </Button>
          </InlineStack>
        </BlockStack>
      </Card>

      {successMessage && (
        <Banner
          tone={savedDecisionResolved ? "success" : "warning"}
          action={
            savedDecisionResolved
              ? {
                  content: t("解決済みを見る"),
                  onAction: () => selectStatus("resolved"),
                }
              : undefined
          }
        >
          {successMessage}
        </Banner>
      )}

      {mismatchQuery.isError ? (
        <QueryError error={mismatchQuery.error} retry={reload} />
      ) : !mismatchQuery.data ? (
        <Card>
          <SkeletonBodyText lines={8} />
        </Card>
      ) : mismatchQuery.data.records.length === 0 ? (
        <Card>
          <Text as="p" tone="subdued">
            {query
              ? t("検索に一致する商品不整合はありません。")
              : t("確認が必要な商品不整合はありません。")}
          </Text>
        </Card>
      ) : (
        <>
          {mismatchQuery.data.records.map((record) => (
            <ProductMismatchCard
              key={[
                record.source_variant_id,
                record.source_snapshot_revision,
                record.latest_revision?.revision ?? 0,
                record.current_target?.variant_id ?? "none",
                record.current_target?.current === true ? "current" : "stale",
                record.operational_application_status,
                record.draft_product?.claim_token ?? "no-draft",
                record.draft_product?.state ?? "none",
              ].join(":")}
              shop={shop}
              record={record}
              reload={reload}
              adjustInventory={adjustInventory}
              onSaved={(response, action) => {
                setSavedDecisionResolved(
                  response.revision.decision === "not_current" ||
                    (response.revision.decision === "mapped" &&
                      response.current_target?.current === true &&
                      response.operational_settings_applied),
                );
                setSuccessMessage(
                  response.revision.decision === "mapped" &&
                    response.current_target?.current !== true
                    ? t(
                        "以前に確認した商品と設定反映の記録はありますが、その商品は現在のShopify商品ではありません。現在の商品を選び直してください。再試行で設定を重複反映していません。",
                      )
                    : response.revision.decision === "mapped" &&
                    response.operational_settings_applied
                    ? action === "settings_only"
                      ? status === "pending"
                        ? t(
                            "現在の商品として確認済みの商品へ、発注設定と仕入条件を確認・反映し、要確認一覧から解決済みに移動しました。税率・在庫数・棚卸し・在庫調整の履歴・既存の発注書・BOM・保存済み原本は変更していません。",
                          )
                        : t(
                            "現在の商品として確認済みの商品へ、発注設定と仕入条件を確認・反映しました。税率・在庫数・棚卸し・在庫調整の履歴・既存の発注書・BOM・保存済み原本は変更していません。",
                          )
                      : status === "pending"
                      ? t(
                          "保存商品と現在の商品が同じだと確認し、発注設定と仕入条件を確認・反映して、要確認一覧から解決済みに移動しました。税率・在庫数・棚卸し・在庫調整の履歴・既存の発注書・BOM・保存済み原本は変更していません。",
                        )
                      : t(
                          "保存商品と現在の商品が同じだと確認し、発注設定と仕入条件を確認・反映しました。税率・在庫数・棚卸し・在庫調整の履歴・既存の発注書・BOM・保存済み原本は変更していません。",
                        )
                    : response.revision.decision === "mapped"
                    ? t(
                        "以前に現在の商品を確認しましたが、最新の保存元の発注設定と仕入条件はまだ確認・反映されていません。現在の商品をもう一度確認してください。",
                      )
                    : status === "pending"
                      ? t(
                          "現在の商品には該当しないと記録しました。要確認一覧から解決済みに移動しました。在庫数や保存済み履歴は変更していません。",
                        )
                      : t(
                          "現在の商品には該当しないと記録しました。在庫数や保存済み履歴は変更していません。",
                        ),
                );
              }}
            />
          ))}
          <Card>
            <InlineStack align="space-between" blockAlign="center" gap="200">
              <Text as="p" variant="bodySm" tone="subdued">
                {mismatchQuery.data.page.total === null
                  ? t("このページ {count} 件", {
                      count: mismatchQuery.data.page.count,
                    })
                  : t("このページ {count} 件 / 全 {total} 件", {
                      count: mismatchQuery.data.page.count,
                      total: mismatchQuery.data.page.total,
                    })}
              </Text>
              <InlineStack gap="200">
                <Button
                  disabled={afterIds.length === 1 || mismatchQuery.isFetching}
                  onClick={() => setAfterIds((current) => current.slice(0, -1))}
                >
                  {t("前のページ")}
                </Button>
                <Button
                  disabled={
                    mismatchQuery.data.page.next_after_id === null ||
                    mismatchQuery.isFetching
                  }
                  onClick={() => {
                    const next = mismatchQuery.data?.page.next_after_id;
                    if (next !== null && next !== undefined) {
                      setAfterIds((current) => [...current, next]);
                    }
                  }}
                >
                  {t("次のページ")}
                </Button>
              </InlineStack>
            </InlineStack>
          </Card>
        </>
      )}
    </BlockStack>
  );
}

function ResourceList({
  shop,
  resource,
  catalogResource,
  openRecord,
}: {
  shop: VerifiedShopGeneration;
  resource: StockyDataResource;
  catalogResource: StockyDataCatalogResource | undefined;
  openRecord: (id: StockyDataRecordId) => void;
}) {
  const queryClient = useQueryClient();
  const [draftQuery, setDraftQuery] = useState("");
  const [query, setQuery] = useState("");
  const [purchaseOrderStatus, setPurchaseOrderStatus] =
    useState<StockyPurchaseOrderStatusFilter>("all");
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const handoffDownload = useDownloadHandoff();
  const [lastDownloadedFilename, setLastDownloadedFilename] = useState<
    string | null
  >(null);
  const cursor = cursors[cursors.length - 1];
  const recordsQuery = useQuery({
    queryKey: [
      "stocky-data",
      shop.shopGeneration,
      "list",
      resource,
      query,
      purchaseOrderStatus,
      cursor,
    ],
    queryFn: () =>
      listStockyDataRecords(
        resource,
        {
          cursor,
          q: query,
          ...(resource === "purchase-orders"
            ? { status: purchaseOrderStatus }
            : {}),
        },
        shop.shopDomain,
      ),
    enabled: catalogResource !== undefined && !catalogResource.masked,
    placeholderData: keepPreviousData,
    retry: retryStockyDataPage,
  });
  const responseMask = recordsQuery.data?.mask?.masked
    ? recordsQuery.data.mask
    : null;
  const catalogMask = catalogPlanMask(catalogResource);
  const effectiveMask = catalogMask ?? responseMask;
  const maskDecisionPending =
    catalogResource === undefined ||
    (effectiveMask === null &&
      (recordsQuery.data === undefined || recordsQuery.isPlaceholderData));
  const controlsDisabled = maskDecisionPending || effectiveMask !== null;
  const exportMutation = useMutation({
    mutationKey: ["stocky-data", shop.shopGeneration, "list-export", resource],
    mutationFn: (exportFormat: "csv" | "pdf") =>
      downloadStockyDataExport(
        resource,
        exportFormat,
        {
          q: query,
          ...(resource === "purchase-orders"
            ? { status: purchaseOrderStatus }
            : {}),
        },
        shop.shopDomain,
      ),
    onSuccess: (download) => {
      handoffDownload(download);
      setLastDownloadedFilename(download.filename);
    },
  });

  useEffect(() => {
    setDraftQuery("");
    setQuery("");
    setPurchaseOrderStatus("all");
    setCursors([null]);
  }, [resource]);

  useEffect(() => {
    if (cursor === null || !isStaleStockyDataCursor(recordsQuery.error)) return;
    void queryClient.invalidateQueries({
      queryKey: [
        "stocky-data",
        shop.shopGeneration,
        "list",
        resource,
        query,
        purchaseOrderStatus,
        null,
      ],
      exact: true,
      refetchType: "none",
    });
    setCursors([null]);
  }, [
    cursor,
    purchaseOrderStatus,
    query,
    queryClient,
    recordsQuery.error,
    resource,
    shop.shopGeneration,
  ]);

  const applySearch = () => {
    setQuery(draftQuery.trim());
    setCursors([null]);
  };
  const clearSearch = () => {
    setDraftQuery("");
    setQuery("");
    setCursors([null]);
  };

  return (
    <Card>
      <BlockStack gap="400">
        <BlockStack gap="100">
          <InlineStack align="space-between" blockAlign="center" gap="200">
            <Text as="h2" variant="headingMd">
              {resourceLabel(resource)}
            </Text>
            <InlineStack gap="200" blockAlign="center" wrap>
              {effectiveMask && (
                <Badge tone="attention">
                  {t("一部の値はプランにより非表示")}
                </Badge>
              )}
              <Button
                loading={
                  exportMutation.isPending && exportMutation.variables === "csv"
                }
                disabled={controlsDisabled || exportMutation.isPending}
                onClick={() => {
                  if (!controlsDisabled) exportMutation.mutate("csv");
                }}
              >
                {t("CSVをダウンロード")}
              </Button>
              <Button
                loading={
                  exportMutation.isPending && exportMutation.variables === "pdf"
                }
                disabled={controlsDisabled || exportMutation.isPending}
                onClick={() => {
                  if (!controlsDisabled) exportMutation.mutate("pdf");
                }}
              >
                {t("PDFをダウンロード")}
              </Button>
            </InlineStack>
          </InlineStack>
          <Text as="p" tone="subdued">
            {t(RESOURCE_UI[resource].description)}
          </Text>
        </BlockStack>
        {lastDownloadedFilename !== null && (
          <Banner
            tone="success"
            onDismiss={() => setLastDownloadedFilename(null)}
          >
            {t("{filename} のダウンロードを開始しました。", {
              filename: lastDownloadedFilename,
            })}
          </Banner>
        )}
        {resource === "purchase-orders" && !controlsDisabled && (
          <PurchaseOrderDashboard
            shop={shop}
            onSelectStatus={(status) => {
              setPurchaseOrderStatus(status);
              setCursors([null]);
            }}
          />
        )}
        <InlineStack gap="200" blockAlign="end" wrap>
          {resource === "purchase-orders" && (
            <Box minWidth="220px">
              <Select
                label={t("状態")}
                options={PURCHASE_ORDER_STATUS_OPTIONS.map((option) => ({
                  value: option.value,
                  label: t(option.label),
                }))}
                value={purchaseOrderStatus}
                onChange={(value) => {
                  setPurchaseOrderStatus(
                    value as StockyPurchaseOrderStatusFilter,
                  );
                  setCursors([null]);
                }}
                disabled={controlsDisabled}
              />
            </Box>
          )}
          <Box minWidth="280px">
            <TextField
              label={t("このデータを検索")}
              value={draftQuery}
              onChange={setDraftQuery}
              autoComplete="off"
              clearButton
              onClearButtonClick={clearSearch}
              maxLength={200}
              disabled={controlsDisabled}
            />
          </Box>
          <Button disabled={controlsDisabled} onClick={applySearch}>
            {t("検索")}
          </Button>
        </InlineStack>
        {query && !controlsDisabled && (
          <Text as="p" variant="bodySm" tone="subdued">
            {t("「{query}」の検索結果", { query })}
          </Text>
        )}
        {exportMutation.isError && (
          <Banner tone="critical" title={t("データを書き出せませんでした")}>
            <p>{errorMessage(exportMutation.error)}</p>
            <p>
              {t("PDFが上限を超える場合は、検索で絞るかCSVをご利用ください。")}
            </p>
          </Banner>
        )}
        {effectiveMask ? (
          <BlockStack gap="300">
            <LockedPreviewTable resource={resource} mask={effectiveMask} />
            <Banner tone="info" title={t("この一覧はプランにより非表示です")}>
              <p>{effectiveMask.reason}</p>
              <p>
                {t("取得開始時 {count} 件・必要なプラン: {plan}", {
                  count: effectiveMask.count,
                  plan: effectiveMask.required_plan,
                })}
              </p>
            </Banner>
          </BlockStack>
        ) : recordsQuery.isError ? (
          <QueryError
            error={recordsQuery.error}
            retry={() => void recordsQuery.refetch()}
          />
        ) : recordsQuery.isLoading || maskDecisionPending ? (
          <SkeletonBodyText lines={8} />
        ) : (
          <BlockStack gap="300">
            {resource === "purchase-orders" ? (
              <PurchaseOrderRecordsTable
                records={recordsQuery.data?.records ?? []}
                onOpen={openRecord}
                query={query}
              />
            ) : (
              <RecordsTable
                records={recordsQuery.data?.records ?? []}
                onOpen={openRecord}
                query={query}
              />
            )}
            {recordsQuery.data && (
              <PageControls
                canPrevious={cursors.length > 1}
                canNext={recordsQuery.data.page.next_cursor !== null}
                loading={recordsQuery.isFetching}
                count={recordsQuery.data.page.count}
                total={recordsQuery.data.page.snapshot_total}
                onPrevious={() => setCursors((current) => current.slice(0, -1))}
                onNext={() => {
                  const next = recordsQuery.data?.page.next_cursor;
                  if (next) setCursors((current) => [...current, next]);
                }}
              />
            )}
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}

const GENERIC_SOURCE_CONTEXT_KEYS = [
  "category",
  "report_category",
  "report_name",
  "report_type",
] as const;

function GenericSourceSummary({ source }: { source: StockyDataGenericSource }) {
  const context = GENERIC_SOURCE_CONTEXT_KEYS.flatMap((key) => {
    const value = source.context[key];
    return value === undefined ? [] : [{ key, value }];
  });
  const statusLabel =
    source.status === "available"
      ? t("取得可能")
      : source.status === "masked"
        ? t("プランにより非表示")
        : t("取得不可");

  return (
    <Box padding="300" background="bg-surface-secondary" borderRadius="200">
      <BlockStack gap="200">
        <InlineStack gap="200" blockAlign="center" wrap>
          <Text as="p" fontWeight="semibold">
            {source.filename}
          </Text>
          <Badge tone={source.status === "available" ? "success" : "attention"}>
            {statusLabel}
          </Badge>
        </InlineStack>
        <Text as="p" variant="bodySm" tone="subdued">
          {t("原本カテゴリ: {category}・保存行数: {rows}", {
            category: source.category,
            rows:
              source.row_count === null
                ? t("行数不明")
                : t("{count}行", { count: source.row_count }),
          })}
        </Text>
        {source.headers.length > 0 && (
          <BlockStack gap="050">
            <Text as="p" variant="bodySm" fontWeight="semibold">
              {t("CSVヘッダー")}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {source.headers.join(" / ")}
            </Text>
          </BlockStack>
        )}
        {context.length > 0 && (
          <BlockStack gap="050">
            <Text as="p" variant="bodySm" fontWeight="semibold">
              {t("取得条件・文脈")}
            </Text>
            {context.map(({ key, value }) => (
              <Text key={key} as="p" variant="bodySm" tone="subdued">
                {fieldLabel(key)}: {value}
              </Text>
            ))}
          </BlockStack>
        )}
        {source.reason && <Text as="p">{source.reason}</Text>}
        {source.required_plan && (
          <Text as="p" variant="bodySm" tone="subdued">
            {t("必要なプラン: {plan}", { plan: source.required_plan })}
          </Text>
        )}
      </BlockStack>
    </Box>
  );
}

function ChildRecords({
  shop,
  resource,
  record,
}: {
  shop: VerifiedShopGeneration;
  resource: StockyDataResource;
  record: StockyDataRecord;
}) {
  const queryClient = useQueryClient();
  const handoffDownload = useDownloadHandoff();
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const cursor = cursors[cursors.length - 1];
  const stocktakeItems = resource === "stocktakes";
  const hasChildren = stocktakeItems || record.links.children !== null;
  const childExportResource: StockyDataChildExportResource | null =
    resource === "purchase-orders" ||
    resource === "goods-receipts" ||
    resource === "stock-adjustments" ||
    resource === "stocktakes"
      ? resource
      : null;
  const childrenQuery = useQuery({
    queryKey: [
      "stocky-data",
      shop.shopGeneration,
      "children",
      resource,
      record.id,
      cursor,
    ],
    queryFn: (): Promise<StockyDataRecordList | StockyDataChildren> =>
      stocktakeItems
        ? listStockyDataRecords(
            "stocktake-items",
            { cursor, parentId: record.id },
            shop.shopDomain,
          )
        : listStockyDataChildren(
            resource,
            record.id,
            { cursor },
            shop.shopDomain,
          ),
    enabled: hasChildren,
    retry: retryStockyDataPage,
  });
  const exportMutation = useMutation({
    mutationKey: [
      "stocky-data",
      shop.shopGeneration,
      "children-export",
      resource,
      record.id,
    ],
    mutationFn: (exportFormat: "csv" | "pdf") => {
      if (childExportResource === null) {
        throw new TypeError("unsupported child export resource");
      }
      return downloadStockyDataChildrenExport(
        childExportResource,
        record.id,
        exportFormat,
        shop.shopDomain,
      );
    },
    onSuccess: handoffDownload,
  });

  useEffect(() => {
    setCursors([null]);
  }, [record.id, resource]);

  useEffect(() => {
    if (cursor === null || !isStaleStockyDataCursor(childrenQuery.error))
      return;
    void queryClient.invalidateQueries({
      queryKey: [
        "stocky-data",
        shop.shopGeneration,
        "children",
        resource,
        record.id,
        null,
      ],
      exact: true,
      refetchType: "none",
    });
    setCursors([null]);
  }, [
    childrenQuery.error,
    cursor,
    queryClient,
    record.id,
    resource,
    shop.shopGeneration,
  ]);

  if (!hasChildren) return null;
  const genericSource =
    childrenQuery.data && "generic_source" in childrenQuery.data
      ? childrenQuery.data.generic_source
      : null;
  const childMask = childrenQuery.data?.mask?.masked
    ? childrenQuery.data.mask
    : null;
  const childrenMasked = childMask !== null;
  const childTitle = stocktakeItems
    ? t("棚卸し明細")
    : resource === "source-metadata"
      ? t("保存済み原本の汎用行")
      : t("明細");
  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center" gap="200" wrap>
          <Text as="h2" variant="headingMd">
            {childTitle}
          </Text>
          {childExportResource !== null &&
            childrenQuery.data !== undefined &&
            !childrenMasked && (
              <InlineStack gap="200" wrap>
                <Button
                  loading={
                    exportMutation.isPending &&
                    exportMutation.variables === "csv"
                  }
                  disabled={exportMutation.isPending}
                  onClick={() => exportMutation.mutate("csv")}
                >
                  {t("明細CSVをダウンロード")}
                </Button>
                <Button
                  loading={
                    exportMutation.isPending &&
                    exportMutation.variables === "pdf"
                  }
                  disabled={exportMutation.isPending}
                  onClick={() => exportMutation.mutate("pdf")}
                >
                  {t("明細PDFをダウンロード")}
                </Button>
              </InlineStack>
            )}
        </InlineStack>
        {resource === "source-metadata" && (
          <Text as="p" tone="subdued">
            {t(
              "原本を運用データへ変換できない場合も、サーバーが保持した解析可能な行を表示します。",
            )}
          </Text>
        )}
        {childrenQuery.isError ? (
          <QueryError
            error={childrenQuery.error}
            retry={() => void childrenQuery.refetch()}
          />
        ) : childrenQuery.isLoading ? (
          <SkeletonBodyText lines={6} />
        ) : (
          <BlockStack gap="300">
            {genericSource && !childrenMasked && (
              <GenericSourceSummary source={genericSource} />
            )}
            {childrenMasked && (
              <Banner tone="info" title={t("明細はプランにより非表示です")}>
                <p>{childMask.reason}</p>
                <p>
                  {t("取得開始時 {count} 件・必要なプラン: {plan}", {
                    count: childMask.count,
                    plan: childMask.required_plan,
                  })}
                </p>
              </Banner>
            )}
            {!childrenMasked && (
              <RecordsTable records={childrenQuery.data?.records ?? []} />
            )}
            {childrenQuery.data && !childrenMasked && (
              <PageControls
                canPrevious={cursors.length > 1}
                canNext={childrenQuery.data.page.next_cursor !== null}
                loading={childrenQuery.isFetching}
                count={childrenQuery.data.page.count}
                total={childrenQuery.data.page.snapshot_total}
                onPrevious={() => setCursors((current) => current.slice(0, -1))}
                onNext={() => {
                  const next = childrenQuery.data?.page.next_cursor;
                  if (next) setCursors((current) => [...current, next]);
                }}
              />
            )}
          </BlockStack>
        )}
        {exportMutation.isError && (
          <Banner tone="critical" title={t("明細を書き出せませんでした")}>
            <p>{errorMessage(exportMutation.error)}</p>
            <p>{t("PDFが上限を超える場合は、CSVをご利用ください。")}</p>
          </Banner>
        )}
      </BlockStack>
    </Card>
  );
}

function HistorySourceRows({
  rows,
  onOpenArchive,
}: {
  rows: StockyHistorySourceRow[];
  onOpenArchive: (archiveId: StockyHistorySourceRow["source_archive_id"]) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          {t("Stocky原本の親行")}
        </Text>
        <Text as="p" variant="bodySm" tone="subdued">
          {t(
            "同じ発注書・在庫調整に一致したCSVの物理行を、行ごとに分けて表示しています。",
          )}
        </Text>
        {rows.map((row) => (
          <Box
            key={`${row.source_archive_id}:${row.source_row_number}`}
            padding="300"
            background="bg-surface-secondary"
            borderRadius="200"
          >
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                {row.filename} · {t("原本行 {row}", { row: row.source_row_number })}
              </Text>
              <InlineGrid columns={{ xs: 1, sm: 2 }} gap="200">
                {row.fields.map((field) => (
                  <BlockStack key={field.key} gap="050">
                    <Text as="span" variant="bodySm" tone="subdued">
                      {fieldLabel(field.key)}
                    </Text>
                    <FieldValue field={field} />
                  </BlockStack>
                ))}
              </InlineGrid>
              <InlineStack align="end">
                <Button
                  size="slim"
                  onClick={() => onOpenArchive(row.source_archive_id)}
                >
                  {t("原本行を開く")}
                </Button>
              </InlineStack>
            </BlockStack>
          </Box>
        ))}
      </BlockStack>
    </Card>
  );
}

interface NoteDialogState {
  kind: StockyNoteKind;
  operation: "set" | "clear";
  text: string;
}

interface NoteAttempt {
  fingerprint: string;
  idempotencyKey: string;
}

function fallbackNoteSlots(
  resource: "suppliers" | "purchase-orders",
): StockyDataNoteSlot[] {
  const kinds: StockyNoteKind[] =
    resource === "suppliers"
      ? ["supplier_private_note", "supplier_po_note_template", "internal_note"]
      : ["purchase_order_note", "internal_note"];
  return kinds.map((kind) => ({
    kind,
    latest_revision: 0,
    has_value: false,
    can_edit: kind === "internal_note",
    links: { revisions: "" },
  }));
}

function NotesPanel({
  shop,
  resource,
  record,
  detailRecordId,
}: {
  shop: VerifiedShopGeneration;
  resource: "suppliers" | "purchase-orders";
  record: StockyDataRecord;
  detailRecordId: StockyDataRecordId;
}) {
  const queryClient = useQueryClient();
  const slots =
    record.note_slots && record.note_slots.length > 0
      ? record.note_slots
      : fallbackNoteSlots(resource);
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const cursor = cursors[cursors.length - 1];
  const [dialog, setDialog] = useState<NoteDialogState | null>(null);
  const [noteConflict, setNoteConflict] = useState(false);
  const [noteConflictRefreshing, setNoteConflictRefreshing] = useState(false);
  const attemptRef = useRef<NoteAttempt | null>(null);

  const refreshNoteState = useCallback(async (): Promise<boolean> => {
    setNoteConflictRefreshing(true);
    setCursors([null]);
    try {
      await Promise.all([
        queryClient.invalidateQueries(
          {
            queryKey: [
              "stocky-data",
              shop.shopGeneration,
              "note-revisions",
              resource,
              record.id,
            ],
          },
          { throwOnError: true },
        ),
        queryClient.invalidateQueries(
          {
            queryKey: [
              "stocky-data",
              shop.shopGeneration,
              "detail",
              resource,
              detailRecordId,
            ],
            exact: true,
          },
          { throwOnError: true },
        ),
      ]);
      return true;
    } catch {
      return false;
    } finally {
      setNoteConflictRefreshing(false);
    }
  }, [detailRecordId, queryClient, record.id, resource, shop.shopGeneration]);

  const revisionsQuery = useQuery({
    queryKey: [
      "stocky-data",
      shop.shopGeneration,
      "note-revisions",
      resource,
      record.id,
      cursor,
    ],
    queryFn: () =>
      listStockyNoteRevisions(resource, record.id, { cursor }, shop.shopDomain),
    placeholderData: keepPreviousData,
    retry: retryStockyDataPage,
  });

  useEffect(() => {
    if (cursor === null || !isStaleStockyDataCursor(revisionsQuery.error)) {
      return;
    }
    void queryClient.invalidateQueries({
      queryKey: [
        "stocky-data",
        shop.shopGeneration,
        "note-revisions",
        resource,
        record.id,
        null,
      ],
      exact: true,
      refetchType: "none",
    });
    setCursors([null]);
  }, [
    cursor,
    queryClient,
    record.id,
    resource,
    revisionsQuery.error,
    shop.shopGeneration,
  ]);

  const mutation = useMutation({
    mutationKey: [
      "stocky-data",
      shop.shopGeneration,
      "note",
      resource,
      record.id,
    ],
    mutationFn: (args: {
      input: CreateStockyNoteRevisionInput;
      idempotencyKey: string;
    }) =>
      createStockyNoteRevision(
        resource,
        record.id,
        args.input,
        args.idempotencyKey,
        shop.shopDomain,
      ),
    onSuccess: () => {
      attemptRef.current = null;
      setNoteConflict(false);
      setDialog(null);
      void queryClient.invalidateQueries({
        queryKey: ["stocky-data", shop.shopGeneration],
      });
    },
    onError: (error) => {
      if (!isStockyNoteConflict(error)) return;
      attemptRef.current = null;
      setDialog(null);
      setNoteConflict(true);
      void refreshNoteState();
    },
  });

  const revisions = revisionsQuery.data?.revisions ?? [];
  const latestLoadedRevision = (kind: StockyNoteKind) =>
    revisions.reduce<StockyNoteRevision | null>(
      (latest, revision) =>
        revision.kind === kind &&
        (latest === null || revision.revision > latest.revision)
          ? revision
          : latest,
      null,
    );
  const isCurrentRevision = (revision: StockyNoteRevision) => {
    const slotRevision =
      slots.find((slot) => slot.kind === revision.kind)?.latest_revision ?? 0;
    const latestRevision =
      slotRevision || latestLoadedRevision(revision.kind)?.revision || 0;
    return revision.revision === latestRevision;
  };
  const canEditRevision = (revision: StockyNoteRevision) =>
    isCurrentRevision(revision) &&
    Boolean(slots.find((slot) => slot.kind === revision.kind)?.can_edit);

  const openDialog = (
    kind: StockyNoteKind,
    existing: StockyNoteRevision | null,
  ) => {
    if (noteConflict || noteConflictRefreshing) return;
    setDialog({
      kind,
      operation: "set",
      text: existing?.operation === "set" ? existing.display_text : "",
    });
    mutation.reset();
  };

  const submit = () => {
    if (!dialog) return;
    const expectedRevision = Math.max(
      slots.find((slot) => slot.kind === dialog.kind)?.latest_revision ?? 0,
      latestLoadedRevision(dialog.kind)?.revision ?? 0,
    );
    const input: CreateStockyNoteRevisionInput = {
      kind: dialog.kind,
      operation: dialog.operation,
      text: dialog.operation === "set" ? dialog.text : null,
      expected_revision: expectedRevision,
    };
    const fingerprint = JSON.stringify({
      resource,
      recordId: record.id,
      input,
    });
    if (attemptRef.current?.fingerprint !== fingerprint) {
      attemptRef.current = {
        fingerprint,
        idempotencyKey: globalThis.crypto.randomUUID(),
      };
    }
    mutation.mutate({
      input,
      idempotencyKey: attemptRef.current.idempotencyKey,
    });
  };

  const noteValidationError = (() => {
    if (!dialog) return null;
    if (dialog.operation === "set") {
      if (dialog.text.trim() === "") return t("メモ本文を入力してください。");
      if (dialog.text.includes("\0"))
        return t("メモ本文にNUL文字は使えません。");
      if (new TextEncoder().encode(dialog.text).byteLength > 65_536) {
        return t("メモ本文はUTF-8で64KiB以内にしてください。");
      }
    }
    return null;
  })();
  const loadedInternalRevision = latestLoadedRevision("internal_note");
  const currentInternalRevision =
    loadedInternalRevision && isCurrentRevision(loadedInternalRevision)
      ? loadedInternalRevision
      : null;

  return (
    <Card>
      <BlockStack gap="400">
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">
            {t("メモ")}
          </Text>
          <Text as="p" tone="subdued">
            {t("保存したメモを確認・編集できます。")}
          </Text>
        </BlockStack>
        {noteConflict && (
          <Banner tone="warning" title={t("メモが別の画面で更新されました")}>
            <BlockStack gap="200">
              <p>
                {t(
                  "最新状態を再読み込みし、内容を確認してからもう一度編集してください。",
                )}
              </p>
              <InlineStack>
                <Button
                  loading={noteConflictRefreshing}
                  disabled={noteConflictRefreshing}
                  onClick={() => {
                    void refreshNoteState().then((refreshed) => {
                      if (refreshed) setNoteConflict(false);
                    });
                  }}
                >
                  {t("最新状態を再読み込み")}
                </Button>
              </InlineStack>
            </BlockStack>
          </Banner>
        )}
        <InlineStack gap="200" wrap>
          <Button
            disabled={noteConflict || noteConflictRefreshing}
            onClick={() => openDialog("internal_note", currentInternalRevision)}
          >
            {currentInternalRevision
              ? t("社内メモを編集")
              : t("社内メモを追加")}
          </Button>
        </InlineStack>
        <Divider />
        <BlockStack gap="200">
          <Text as="h3" variant="headingSm">
            {t("保存したメモ")}
          </Text>
          {revisionsQuery.isError ? (
            <QueryError
              error={revisionsQuery.error}
              retry={() => void revisionsQuery.refetch()}
            />
          ) : revisionsQuery.isLoading ? (
            <SkeletonBodyText lines={4} />
          ) : revisions.length === 0 ? (
            <Text as="p" tone="subdued">
              {t("保存したメモはありません。")}
            </Text>
          ) : (
            <BlockStack gap="200">
              {revisions.map((revision) => (
                <Box
                  key={revision.id}
                  padding="300"
                  background="bg-surface-secondary"
                  borderRadius="200"
                >
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center" wrap>
                      <Text as="span" fontWeight="semibold">
                        {noteKindLabel(revision.kind)}
                      </Text>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {t("更新: {date}", {
                          date: formatDateTime(revision.created_at),
                        })}
                      </Text>
                      {canEditRevision(revision) && (
                        <Button
                          variant="plain"
                          disabled={noteConflict || noteConflictRefreshing}
                          onClick={() => openDialog(revision.kind, revision)}
                        >
                          {t("編集")}
                        </Button>
                      )}
                    </InlineStack>
                    <Text as="p">
                      {revision.operation === "clear"
                        ? t("この更新でメモを空にしました。")
                        : revision.display_text}
                    </Text>
                  </BlockStack>
                </Box>
              ))}
              {revisionsQuery.data && (
                <PageControls
                  canPrevious={cursors.length > 1}
                  canNext={revisionsQuery.data.page.next_cursor !== null}
                  loading={revisionsQuery.isFetching}
                  count={revisionsQuery.data.page.count}
                  total={revisionsQuery.data.page.snapshot_total}
                  onPrevious={() =>
                    setCursors((current) => current.slice(0, -1))
                  }
                  onNext={() => {
                    const next = revisionsQuery.data?.page.next_cursor;
                    if (next) setCursors((current) => [...current, next]);
                  }}
                />
              )}
            </BlockStack>
          )}
        </BlockStack>
      </BlockStack>

      <Modal
        open={dialog !== null}
        onClose={() => {
          if (!mutation.isPending) setDialog(null);
        }}
        title={dialog ? noteKindLabel(dialog.kind) : t("メモ")}
        primaryAction={{
          content: t("保存"),
          onAction: submit,
          loading: mutation.isPending,
          disabled: dialog === null || noteValidationError !== null,
        }}
        secondaryActions={[
          {
            content: t("キャンセル"),
            onAction: () => setDialog(null),
            disabled: mutation.isPending,
          },
        ]}
      >
        <Modal.Section>
          {dialog && (
            <BlockStack gap="300">
              <Select
                label={t("操作")}
                options={[
                  { label: t("新しい本文を保存"), value: "set" },
                  { label: t("現在の表示値を空にする"), value: "clear" },
                ]}
                value={dialog.operation}
                onChange={(value) =>
                  setDialog((current) =>
                    current
                      ? { ...current, operation: value as "set" | "clear" }
                      : current,
                  )
                }
                disabled={mutation.isPending}
              />
              {dialog.operation === "set" ? (
                <TextField
                  label={t("メモ本文")}
                  value={dialog.text}
                  onChange={(text) =>
                    setDialog((current) =>
                      current ? { ...current, text } : current,
                    )
                  }
                  multiline={6}
                  autoComplete="off"
                  maxLength={20_000}
                  showCharacterCount
                  disabled={mutation.isPending}
                />
              ) : (
                <Banner tone="warning">
                  <p>
                    {t(
                      "現在のメモを空にします。以前の内容は履歴に保持されます。",
                    )}
                  </p>
                </Banner>
              )}
              {noteValidationError && (
                <Text as="p" tone="critical">
                  {noteValidationError}
                </Text>
              )}
              {mutation.isError && (
                <Banner tone="critical" title={t("メモを保存できませんでした")}>
                  <p>{errorMessage(mutation.error)}</p>
                </Banner>
              )}
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
    </Card>
  );
}

function RecordDetail({
  shop,
  resource,
  recordId,
  catalogResource,
  openDataImport,
}: {
  shop: VerifiedShopGeneration;
  resource: StockyDataResource;
  recordId: StockyDataRecordId;
  catalogResource: StockyDataCatalogResource | undefined;
  openDataImport: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const handoffDownload = useDownloadHandoff();
  const sourceDownloadMutation = useMutation({
    mutationKey: [
      "stocky-data",
      shop.shopGeneration,
      "source-download",
      resource,
      recordId,
    ],
    mutationFn: (archive: StockyDataSourceDownload) =>
      downloadStockySourceArchive(archive),
    onSuccess: handoffDownload,
  });
  const detailQuery = useQuery({
    queryKey: [
      "stocky-data",
      shop.shopGeneration,
      "detail",
      resource,
      recordId,
    ],
    queryFn: () => getStockyDataRecord(resource, recordId, shop.shopDomain),
    enabled: catalogResource !== undefined && !catalogResource.masked,
  });
  let exactSavedDraftSourceId: SavedDraftSourceId | null = null;
  if (resource === "purchase-orders") {
    try {
      exactSavedDraftSourceId = canonicalSavedDraftSourceId(recordId);
    } catch {
      exactSavedDraftSourceId = null;
    }
  }
  const savedDraftsQuery = useQuery({
    queryKey: [
      "saved-purchase-order-drafts",
      "source",
      exactSavedDraftSourceId,
    ],
    queryFn: () => listSavedPurchaseOrderDrafts(exactSavedDraftSourceId!),
    enabled:
      exactSavedDraftSourceId !== null &&
      catalogResource !== undefined &&
      !catalogResource.masked,
  });
  const responseMask = detailQuery.data?.mask?.masked
    ? detailQuery.data.mask
    : null;
  const effectiveMask = catalogPlanMask(catalogResource) ?? responseMask;

  if (effectiveMask) {
    return (
      <Banner tone="info" title={t("この詳細はプランにより非表示です")}>
        <p>{effectiveMask.reason}</p>
        <p>
          {t("保存件数 {count} 件・必要なプラン: {plan}", {
            count: effectiveMask.count,
            plan: effectiveMask.required_plan,
          })}
        </p>
      </Banner>
    );
  }

  if (detailQuery.isError) {
    return (
      <QueryError
        error={detailQuery.error}
        retry={() => void detailQuery.refetch()}
      />
    );
  }
  if (!detailQuery.data) {
    return (
      <Card>
        <SkeletonBodyText lines={8} />
      </Card>
    );
  }
  if (detailQuery.data.record === null) {
    return (
      <Banner tone="critical" title={t("保存済み詳細を表示できません")}>
        <p>{t("このストアに属するレコードを確認できませんでした。")}</p>
      </Banner>
    );
  }
  const record = detailQuery.data.record;
  const sourceDownloads = detailQuery.data.source_downloads;
  const sourceRows = detailQuery.data.source_rows ?? [];
  const supportsNotes =
    (resource === "suppliers" || resource === "purchase-orders") &&
    record.links.note_revisions !== null;
  const savedDraftSummary =
    resource === "purchase-orders"
      ? savedDraftsQuery.data?.drafts.find(
          (draft) => draft.source_id === String(record.id),
        ) ?? null
      : null;

  return (
    <BlockStack gap="400">
      {resource === "purchase-orders" &&
        exactSavedDraftSourceId !== null &&
        savedDraftsQuery.isError && (
          <Banner
            tone="critical"
            title={t("保存済み下書きを取得できませんでした")}
            action={{
              content: t("再試行"),
              onAction: () => void savedDraftsQuery.refetch(),
            }}
          >
            <p>{errorMessage(savedDraftsQuery.error)}</p>
          </Banner>
        )}
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="start" gap="300" wrap>
            <BlockStack gap="100">
              <Text as="h2" variant="headingLg">
                {merchantPrimaryText(record)}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {t("{resource} の保存済み詳細", {
                  resource: resourceLabel(resource),
                })}
              </Text>
            </BlockStack>
            {resource === "purchase-orders" &&
            exactSavedDraftSourceId !== null &&
            savedDraftsQuery.isLoading ? (
              <Button variant="primary" loading disabled>
                {t("注文書として編集")}
              </Button>
            ) : savedDraftSummary !== null ? (
              <Button
                variant="primary"
                onClick={() =>
                  navigate({
                    pathname: "/purchase-orders",
                    search: shopSearch(
                      location.search,
                      savedDraftSummary.id === null
                        ? {
                            view: "saved",
                            source: savedDraftSummary.source_id,
                          }
                        : {
                            view: "saved",
                            draft: canonicalStockyPublicBigIntId(
                              savedDraftSummary.id,
                            ),
                          },
                    ),
                  })
                }
              >
                {t("注文書として編集")}
              </Button>
            ) : null}
          </InlineStack>
          <CurrentProductOverlay record={record} />
          <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
            {record.fields
              .filter(
                (field) =>
                  isMerchantVisibleField(record, field) &&
                  !isProductIdentityOverlayField(field),
              )
              .map((field) => (
                <Box
                  key={field.key}
                  padding="300"
                  background="bg-surface-secondary"
                  borderRadius="200"
                >
                  <BlockStack gap="100">
                    <Text as="span" variant="bodySm" tone="subdued">
                      {fieldLabel(field.key)}
                    </Text>
                    <FieldValue field={field} />
                  </BlockStack>
                </Box>
              ))}
          </InlineGrid>
          {record.updated_at && (
            <Text as="p" variant="bodySm" tone="subdued">
              {t("保存データ更新: {date}", {
                date: formatDateTime(record.updated_at),
              })}
            </Text>
          )}
        </BlockStack>
      </Card>

      {supportsNotes && (
        <NotesPanel
          shop={shop}
          resource={resource}
          record={record}
          detailRecordId={recordId}
        />
      )}

      <ChildRecords shop={shop} resource={resource} record={record} />

      <HistorySourceRows
        rows={sourceRows}
        onOpenArchive={(archiveId) =>
          navigate({
            pathname: `/stocky-data/source-metadata/${archiveId}`,
            search: shopSearch(location.search),
          })
        }
      />

      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center" gap="200">
            <Text as="h2" variant="headingMd">
              {t("関連する保存済み原本")}
            </Text>
            <Button onClick={openDataImport}>{t("データ取込を開く")}</Button>
          </InlineStack>
          {sourceDownloads.length === 0 ? (
            <Text as="p" tone="subdued">
              {t("このレコードに直接紐づく保存済み原本はありません。")}
            </Text>
          ) : (
            <BlockStack gap="200">
              {sourceDownloads.map((archive) => (
                <Box
                  key={archive.id}
                  padding="300"
                  background="bg-surface-secondary"
                  borderRadius="200"
                >
                  <BlockStack gap="100">
                    <Text as="p" fontWeight="semibold">
                      {archive.filename}
                    </Text>
                    {archive.created_at && (
                      <Text as="p" variant="bodySm" tone="subdued">
                        {t("保存日時: {date}", {
                          date: formatDateTime(archive.created_at),
                        })}
                      </Text>
                    )}
                    <Text as="p" variant="bodySm" tone="subdued">
                      {t("原本サイズ: {size} bytes・SHA-256: {sha256}", {
                        size: archive.size_bytes.toLocaleString(),
                        sha256: archive.sha256,
                      })}
                    </Text>
                    <InlineStack gap="200">
                      <Button
                        variant="plain"
                        loading={
                          sourceDownloadMutation.isPending &&
                          sourceDownloadMutation.variables?.id === archive.id
                        }
                        disabled={sourceDownloadMutation.isPending}
                        onClick={() => sourceDownloadMutation.mutate(archive)}
                      >
                        {t("原本をダウンロード")}
                      </Button>
                    </InlineStack>
                  </BlockStack>
                </Box>
              ))}
            </BlockStack>
          )}
          {sourceDownloadMutation.isError && (
            <Banner
              tone="critical"
              title={t("原本をダウンロードできませんでした")}
            >
              <p>{errorMessage(sourceDownloadMutation.error)}</p>
            </Banner>
          )}
        </BlockStack>
      </Card>
    </BlockStack>
  );
}

function StockyDataForShop({ shop }: { shop: VerifiedShopGeneration }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ resource?: string; recordId?: string }>();
  const resource = isStockyDataResource(params.resource)
    ? params.resource
    : null;
  const recordIdText = params.recordId;
  const recordId =
    resource === null ? null : parseStockyDataRecordId(resource, recordIdText);
  const validRecordId =
    recordIdText === undefined || (resource !== null && recordId !== null);
  const contextSearch = shopSearch(location.search);
  const navigateTo = (pathname: string, extras?: Record<string, string>) =>
    navigate({ pathname, search: shopSearch(location.search, extras) });

  const catalogQuery = useQuery({
    queryKey: ["stocky-data", shop.shopGeneration, "catalog"],
    queryFn: () => getStockyDataCatalog(shop.shopDomain),
  });
  const catalog = catalogQuery.data?.resources ?? [];
  const catalogByKey = new Map(
    catalog.map((catalogResource) => [catalogResource.key, catalogResource]),
  );

  if (
    (params.resource !== undefined && resource === null) ||
    !validRecordId ||
    (resource === "product-mismatches" && recordId !== null)
  ) {
    return (
      <Page title={t("データ")}>
        <Banner
          tone="critical"
          title={t("指定されたデータは見つかりません")}
          action={{
            content: t("データへ戻る"),
            onAction: () => navigateTo("/stocky-data"),
          }}
        />
      </Page>
    );
  }

  const openDataImport = () => navigateTo("/stocky-csv");

  return (
    <Page
      title={t("データ")}
      subtitle={t(
        "Stockyから保存・取り込んだ過去のデータと、これからUreyukiBoxで作成するデータを種類別に確認します。",
      )}
      backAction={
        recordId !== null && resource
          ? {
              content: resourceLabel(resource),
              onAction: () =>
                navigate({
                  pathname: `/stocky-data/${resource}`,
                  search: contextSearch,
                }),
            }
          : resource
            ? {
                content: t("データ"),
                onAction: () =>
                  navigate({ pathname: "/stocky-data", search: contextSearch }),
              }
            : undefined
      }
    >
      <Layout>
        {resource !== null && (
          <Layout.Section variant="oneThird">
            <Card>
              {catalogQuery.isError ? (
                <QueryError
                  error={catalogQuery.error}
                  retry={() => void catalogQuery.refetch()}
                />
              ) : catalogQuery.isLoading ? (
                <SkeletonBodyText lines={10} />
              ) : (
                <ResourceNavigation
                  selected={resource}
                  catalog={catalog}
                  navigateTo={(next) => navigateTo(`/stocky-data/${next}`)}
                />
              )}
            </Card>
          </Layout.Section>
        )}
        <Layout.Section>
          {resource === null ? (
            <BlockStack gap="400">
              {catalogQuery.isError ? (
                <Card>
                  <QueryError
                    error={catalogQuery.error}
                    retry={() => void catalogQuery.refetch()}
                  />
                </Card>
              ) : catalogQuery.isLoading ? (
                <Card>
                  <SkeletonBodyText lines={10} />
                </Card>
              ) : null}
              <Banner
                tone="info"
                title={t(
                  "過去のStockyデータと、これからのUreyukiBoxデータをまとめて確認できます",
                )}
              >
                <p>
                  {t(
                    "Stockyから保存・取り込んだ履歴に加え、UreyukiBoxで作成する発注書・入荷履歴・在庫調整・棚卸しも同じ種類別の一覧に入ります。仕入先、発注点・リードタイム・最低発注数量（MOQ）、現在の仕入税率もここで確認できます。",
                  )}
                </p>
              </Banner>
              {!catalogQuery.isLoading &&
                !catalogQuery.isError &&
                DATA_SECTIONS.map((group) => (
                  <Card key={group.key}>
                    <BlockStack gap="300">
                      <BlockStack gap="100">
                        <Text as="h2" variant="headingMd">
                          {t(group.title)}
                        </Text>
                        <Text as="p" tone="subdued">
                          {t(group.description)}
                        </Text>
                      </BlockStack>
                      <InlineStack gap="200" wrap>
                        {group.resources.map((nextResource) => {
                          const info = catalogByKey.get(nextResource);
                          return (
                            <Button
                              key={nextResource}
                              onClick={() =>
                                navigateTo(`/stocky-data/${nextResource}`)
                              }
                            >
                              {t("{label}（{count}件）", {
                                label: resourceLabel(nextResource),
                                count: info?.count ?? 0,
                              })}
                            </Button>
                          );
                        })}
                      </InlineStack>
                    </BlockStack>
                  </Card>
                ))}
            </BlockStack>
          ) : resource === "product-mismatches" ? (
            <ProductMismatchList
              key={`${shop.shopGeneration}:${resource}`}
              shop={shop}
              adjustInventory={(shopifyVariantId) =>
                navigate({
                  pathname: "/inventory-adjustments",
                  search: shopSearch(location.search, {
                    variant_id: String(shopifyVariantId),
                  }),
                })
              }
            />
          ) : recordId === null ? (
            <ResourceList
              key={`${shop.shopGeneration}:${resource}`}
              shop={shop}
              resource={resource}
              catalogResource={catalog.find((item) => item.key === resource)}
              openRecord={(id) => {
                if (!isStockyDataRecordId(resource, id)) return;
                navigateTo(
                  `/stocky-data/${resource}/${encodeStockyDataRecordId(resource, id)}`,
                );
              }}
            />
          ) : (
            <RecordDetail
              key={`${shop.shopGeneration}:${resource}:${recordId}`}
              shop={shop}
              resource={resource}
              recordId={recordId}
              catalogResource={catalogByKey.get(resource)}
              openDataImport={openDataImport}
            />
          )}
        </Layout.Section>
      </Layout>
    </Page>
  );
}

/** A hard remount at the verified shop generation prevents A state entering B. */
export function StockyData() {
  const shop = useVerifiedShopGeneration();
  const { entitlementGeneration } = useVerifiedEntitlementGeneration();
  const authorityQuery = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    enabled: entitlementGeneration !== "explicit",
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });
  const responseGeneration = authorityQuery.data
    ? stockyEntitlementGeneration(authorityQuery.data)
    : entitlementGeneration === "explicit"
      ? "explicit"
      : null;

  // A cached paid page must never paint while current plan authority is being
  // refreshed. The tenant boundary removes the old generation before it
  // accepts a changed subscription; this local gate also protects first mount
  // and window-focus refreshes.
  if (entitlementGeneration !== "explicit" && authorityQuery.isFetching) {
    return (
      <Page title={t("データ")}>
        <SkeletonBodyText lines={8} />
      </Page>
    );
  }
  if (
    authorityQuery.error !== null ||
    responseGeneration === null ||
    responseGeneration !== entitlementGeneration
  ) {
    return (
      <Page title={t("データ")}>
        <Banner
          tone="critical"
          title={t("現在のプランを確認できませんでした")}
          action={{
            content: t("再試行"),
            onAction: () => void authorityQuery.refetch(),
          }}
        >
          <p>{t("安全のため、データは表示していません。")}</p>
        </Banner>
      </Page>
    );
  }

  const scopedShop: VerifiedShopGeneration = {
    ...shop,
    shopGeneration: `${shop.shopGeneration}:entitlement:${entitlementGeneration}`,
  };
  return (
    <StockyDataForShop key={scopedShop.shopGeneration} shop={scopedShop} />
  );
}
